// src/documents/documents.client.ts — paginile #/documents, #/registre, #/sabloane.
// Un singur modul cu trei secțiuni (alese din rută): documente (generare din șablon,
// înregistrare manuală, legături), registre (numerotare incrementală per hartă) și
// șabloane docx (upload/versiuni/placeholders — globale). Documentele și registrele
// sunt scoped pe workspace-ul global (harta selectată în AppHeader).

import "../styles.css"
import "@phosphor-icons/web/regular"
import van from "vanjs-core"
import * as vanX from "vanjs-ext"
import { current_path, get_hash_query_params, navigate } from "../lib/router.client"
import { workspace } from "../lib/workspace.client"
import { session, session_loaded } from "../lib/session.client"
import { format_area } from "../lib/geodesy"
import type { Rapoarte } from "../rapoarte"

const t = van.tags

// Rolurile de org care pot gestiona șabloane (editor+); super gestionează și globalele.
const TPL_WRITE_ROLES = new Set(["owner", "admin", "editor"])

/**
 * Oglinda EXACTĂ a lui `gate_template` (src/tenancy.ts) pentru SCRIERE (câmpuri, fișier, ștergere):
 * super → da; șablon GLOBAL (org_id null) → nimeni altcineva, nici support; șablon de primărie →
 * doar membru al org-ului cu rol de scriere (TPL_WRITE_ROLES — aceeași listă ca la picker, nu o a
 * doua definiție). Serverul rămâne poarta reală — interfața doar nu mai oferă butoane care se
 * termină în 403 (și în editări pierdute). Citește `session.val` → REACTIVĂ: sesiunea se hidratează
 * asincron, iar la un reload direct pe #/sabloane cardul se randează înainte să se știe cine ești.
 */
function can_manage_template(tpl: { org_id: string | null }): boolean {
	const s = session.val
	if (s.user?.platform_role === "super") {
		return true
	}
	if (tpl.org_id == null) {
		return false
	}
	return s.memberships.some((m) => m.org_id === tpl.org_id && TPL_WRITE_ROLES.has(m.role))
}

/** De ce e „doar citire" pentru utilizatorul curent (afișat în locul butoanelor de scriere). */
function readonly_reason(tpl: { org_id: string | null }): string {
	if (tpl.org_id == null) {
		return "Doar citire — șablon global de sistem, se modifică de administratorul platformei"
	}
	return `Doar citire — nu ai rol de editor în ${org_name_of(tpl.org_id, "această primărie")}`
}

// Numele org-urilor (id → nume) pentru badge-urile cardurilor și pentru picker: super → O SINGURĂ
// cerere /api/admin/orgs per montare (ruta e doar-super; support ia refuz → rămâne „Organizație");
// ceilalți le au în sesiune. Nu se memorează un apel făcut ÎNAINTE de hidratare (sesiunea „anonimă"
// nu e încă super) — lista se re-cere la prima randare de după hidratare.
let org_names_state: "idle" | "loading" | "done" = "idle"
let org_names_promise: Promise<void> = Promise.resolve()
function load_org_names(): Promise<void> {
	if (org_names_state !== "idle") {
		return org_names_promise
	}
	if (session.val.user?.platform_role !== "super") {
		return Promise.resolve()
	}
	org_names_state = "loading"
	org_names_promise = (async () => {
		const r = await api_get<Array<{ id: string; name: string }>>("/api/admin/orgs")
		if (r.ok) {
			const names: Record<string, string> = {}
			for (const o of r.data) {
				names[o.id] = o.name
			}
			store.org_names = names
		}
		org_names_state = "done"
	})()
	return org_names_promise
}
/** Numele org-ului din lista adusă sau din sesiune; altfel `fallback` („Organizație" pe badge). */
function org_name_of(org_id: string, fallback = "Organizație"): string {
	return (
		store.org_names[org_id] ??
		session.val.memberships.find((m) => m.org_id === org_id)?.org_name ??
		fallback
	)
}

/**
 * Opțiunile de scope pentru un șablon nou: super → „Global (sistem)" + toate org-urile;
 * altfel doar org-urile în care e editor+. value="" înseamnă global (doar super).
 */
async function template_scope_options(): Promise<Array<{ value: string; label: string }>> {
	if (session.val.user?.platform_role === "super") {
		await load_org_names() // aceeași listă ca badge-urile — o singură cerere per montare
		const orgs = Object.entries(store.org_names).map(([value, label]) => ({ value, label }))
		return [{ value: "", label: "Global (sistem)" }, ...orgs]
	}
	return session.val.memberships
		.filter((m) => TPL_WRITE_ROLES.has(m.role))
		.map((m) => ({ value: m.org_id, label: m.org_name }))
}

/** Icon Phosphor inline — <i class="ph ph-<nume>"> (fontul vine din @phosphor-icons/web). */
function icon(name: string) {
	return t.i({ class: `ph ph-${name}` })
}

// ─────────────────────────────────────────────
// Tipuri (oglindesc API-ul din src/documents.ts / src/links.ts)
// ─────────────────────────────────────────────

type ApiOk<D> = { _s?: "v"; ok: true; data: D }
type ApiErr = {
	_s?: "v"
	ok: false
	err?: { code: string; message?: string; http_status_code?: number }
}
type ApiResult<D> = ApiOk<D> | ApiErr

interface PlaceholderInfo {
	code: string
	type: string
}

type TemplateFieldType = "string" | "number" | "boolean" | "date" | "list"

interface TemplateField {
	key: string
	label?: string
	type?: TemplateFieldType
	description?: string
	default?: unknown
	source?: string
	required?: boolean
}

interface TemplateSummary {
	id: string
	slug: string
	org_id: string | null // NULL = global de sistem; setat = al unei organizații
	name: string
	description: string | null
	filename: string
	size: number
	version: number
	placeholders: PlaceholderInfo[]
	fields: TemplateField[]
	created_at: string
}

interface EntityContext {
	entity: { id: string; kind: string; slug: string | null; name: string }
	map?: { id: string; slug: string | null; name: string }
	layer?: { id: string; slug: string | null; name: string }
	attributes: Record<string, unknown>
	config: Record<string, unknown>
	metadata: Record<string, unknown>
	flat: Record<string, unknown>
}

interface DocumentSummary {
	id: string
	name: string
	filename: string
	size: number
	template_id: string | null
	entity_id: string | null
	map_id: string | null
	registry_code: string // "" = fără număr de registru
	status: "emis" | "anulat"
	registered: boolean
	is_public: boolean // expus în portalul public (F6, documentația scanată — opt-in strict)
	created_at: string
}

interface DocumentDetail extends DocumentSummary {
	data: Record<string, unknown> | null
	version: number
}

interface DocumentVersionInfo {
	version: number
	change_type: string
	author: string
	message: string
	source: string
	status: "emis" | "anulat"
	size: number
	created_at: string
}

interface RegistrySummary {
	id: string
	slug: string
	name: string
	prefix: string
	padding: number
	suffix: string
	number_field: string
	description: string
	document_count: number
	last_code: string
	created_at: string
}

interface RegistryEntry {
	id: string
	registry_code: string
	registry_number: number
	name: string
	filename: string
	size: number
	status: "emis" | "anulat"
	created_at: string
}

interface RefInfo {
	table: string
	id: string
	name: string
	kind: string | null
}

interface LinkInfo {
	id: string
	rel: string
	direction: "out" | "in"
	other: RefInfo | null
	created_at: string
}

interface MapSummary {
	id: string
	slug: string
	name: string
}

// ─────────────────────────────────────────────
// State global reactiv
// ─────────────────────────────────────────────

const store = vanX.reactive({
	active_tab: "documents" as "templates" | "documents" | "registries" | "rapoarte",

	// Rapoarte per hartă (F5-T9) — scoped pe workspace.
	rapoarte: null as Rapoarte | null,
	rapoarte_loading: false,
	// Refuzul serverului (ex. 403 de contract, Faza 2) — altfel pagina ar randa tabele goale
	// care arată a pierdere de date, nu a modul neinclus.
	rapoarte_error: "",

	templates: [] as TemplateSummary[],
	templates_loading: false,

	documents: [] as DocumentSummary[],
	documents_loading: false,

	// Filtre listă documente (combinabile cu workspace-ul)
	filter_q: "",
	filter_status: "" as "" | "emis" | "anulat",
	filter_template_id: "",
	filter_registry_id: "",
	// Filtrul pe ENTITATE (deep link #/documents?entity=<id> — din popup-ul actelor de pe hartă sau
	// din panoul parcelei): lista arată DOAR documentele parcelei, cu un cip vizibil de renunțare;
	// `&doc=<id>` evidențiază actul pe care s-a dat click. Independent de entitatea de generare.
	filter_entity_id: "",
	filter_entity_label: "",
	highlight_doc_id: "",
	highlight_scroll: false, // derulează la act O SINGURĂ dată (la sosire), nu la fiecare reîncărcare a listei

	// Entitatea GIS legată — se alege PE HARTĂ (deep link #/documents?entity=<id>);
	// aici doar se afișează și se poate deselecta.
	gen_entity: { id: "", name: "", layer_name: "" },

	maps: [] as MapSummary[],

	// Registre (scoped pe workspace)
	registries: [] as RegistrySummary[],
	registries_loading: false,
	expanded_registry: "" as string, // slug-ul registrului cu tabelul deschis
	registry_entries: [] as RegistryEntry[],
	registry_entries_loading: false,

	// Generare
	gen_template: "", // slug-ul șablonului selectat
	gen_registry: "", // slug-ul registrului care alocă numărul (opțional)
	gen_loading: false,
	last_generated: null as DocumentSummary | null,

	// Rectificare (regenerare cu același număr): documentul țintă + datele lui
	regen_doc_id: "" as string,
	regen_doc_label: "" as string, // ex. "CU0003/2026" sau numele
	regen_data: null as Record<string, unknown> | null,

	// Document expandat (legături + înregistrare în registru + istoric)
	expanded_doc: "" as string,
	doc_links: [] as LinkInfo[],
	doc_links_loading: false,
	doc_history: [] as DocumentVersionInfo[],
	doc_history_visible: "" as string, // id-ul documentului cu istoricul deschis
	doc_history_loading: false,

	// Înlocuire fișier șablon / editor de câmpuri (slug-ul în curs de editare)
	replacing_template: "" as string,
	editing_fields_template: "" as string,
	// id → numele primăriei (badge-urile cardurilor de șablon + picker-ul de vizibilitate; vezi load_org_names)
	org_names: {} as Record<string, string>,
})

// Input-urile formularului de generare, per cheie de câmp (ne-reactive: citite la submit).
let gen_inputs: Record<string, HTMLInputElement | HTMLTextAreaElement> = {}

// ─────────────────────────────────────────────
// API helpers
// ─────────────────────────────────────────────

async function api_get<D>(url: string): Promise<ApiResult<D>> {
	try {
		const res = await fetch(url)
		return (await res.json()) as ApiResult<D>
	} catch (e) {
		return { ok: false, err: { code: "NETWORK_ERROR", message: String(e) } }
	}
}

async function api_post<D>(url: string, body: unknown): Promise<ApiResult<D>> {
	try {
		const res = await fetch(url, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(body),
		})
		return (await res.json()) as ApiResult<D>
	} catch (e) {
		return { ok: false, err: { code: "NETWORK_ERROR", message: String(e) } }
	}
}

async function api_patch<D>(url: string, body: unknown): Promise<ApiResult<D>> {
	try {
		const res = await fetch(url, {
			method: "PATCH",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(body),
		})
		return (await res.json()) as ApiResult<D>
	} catch (e) {
		return { ok: false, err: { code: "NETWORK_ERROR", message: String(e) } }
	}
}

async function api_upload<D>(url: string, fd: FormData): Promise<ApiResult<D>> {
	try {
		const res = await fetch(url, { method: "POST", body: fd })
		return (await res.json()) as ApiResult<D>
	} catch (e) {
		return { ok: false, err: { code: "NETWORK_ERROR", message: String(e) } }
	}
}

async function api_delete<D>(url: string): Promise<ApiResult<D>> {
	try {
		const res = await fetch(url, { method: "DELETE" })
		return (await res.json()) as ApiResult<D>
	} catch (e) {
		return { ok: false, err: { code: "NETWORK_ERROR", message: String(e) } }
	}
}

// ─────────────────────────────────────────────
// Toast
// ─────────────────────────────────────────────

function show_toast(message: string, kind: "ok" | "err" | "info" = "info", duration = 4500) {
	const area = document.getElementById("doc-toast-area")
	if (!area) {
		return
	}
	const kind_cls = {
		ok: "bg-emerald-50 dark:bg-emerald-950 border-l-4 border-emerald-500 text-emerald-800 dark:text-emerald-300",
		err: "bg-red-50 dark:bg-red-950 border-l-4 border-red-500 text-red-800",
		info: "bg-slate-50 dark:bg-slate-950 border-l-4 border-slate-400 text-slate-700 dark:text-slate-200",
	}[kind]
	const el = t.div(
		{
			class: `flex items-start gap-3 px-4 py-3 rounded-lg shadow-lg text-sm font-medium max-w-sm pointer-events-auto ${kind_cls}`,
		},
		message,
	)
	van.add(area, el)
	setTimeout(() => el.remove(), duration)
}

// ─────────────────────────────────────────────
// Încărcare date
// ─────────────────────────────────────────────

async function load_templates() {
	store.templates_loading = true
	const r = await api_get<TemplateSummary[]>("/api/templates")
	if (r.ok) {
		store.templates = r.data
	} else {
		show_toast(`Eroare la încărcarea șabloanelor: ${r.err?.message ?? ""}`, "err")
	}
	store.templates_loading = false
}

async function load_documents() {
	store.documents_loading = true
	const qs = new URLSearchParams()
	if (workspace.val.slug) {
		qs.set("map", workspace.val.slug)
	}
	if (store.filter_q.trim()) {
		qs.set("q", store.filter_q.trim())
	}
	if (store.filter_status) {
		qs.set("status", store.filter_status)
	}
	if (store.filter_template_id) {
		qs.set("template", store.filter_template_id)
	}
	if (store.filter_registry_id) {
		qs.set("registry_id", store.filter_registry_id)
	}
	if (store.filter_entity_id) {
		qs.set("entity", store.filter_entity_id) // numele parametrului rutei GET /api/documents
	}
	const q = qs.toString()
	const r = await api_get<DocumentSummary[]>(`/api/documents${q ? "?" + q : ""}`)
	if (r.ok) {
		store.documents = r.data
		// Actul venit din popup-ul hărții: adus în vedere după ce lista s-a desenat — o singură dată,
		// altfel orice schimbare de filtru sau reîncărcare sărea lista înapoi la el.
		if (store.highlight_doc_id && store.highlight_scroll) {
			store.highlight_scroll = false
			const id = store.highlight_doc_id
			requestAnimationFrame(() =>
				document.getElementById(`doc-${id}`)?.scrollIntoView({ block: "center" }),
			)
		}
	} else {
		show_toast(`Eroare la încărcarea documentelor: ${r.err?.message ?? ""}`, "err")
	}
	store.documents_loading = false
}

async function load_registries() {
	if (!workspace.val.slug) {
		store.registries = []
		return
	}
	store.registries_loading = true
	const r = await api_get<RegistrySummary[]>(
		`/api/maps/${encodeURIComponent(workspace.val.slug)}/registries`,
	)
	if (r.ok) {
		store.registries = r.data
	} else {
		show_toast(`Eroare la încărcarea registrelor: ${r.err?.message ?? ""}`, "err")
	}
	store.registries_loading = false
}

async function load_registry_entries(slug: string) {
	store.registry_entries_loading = true
	store.registry_entries = []
	const r = await api_get<RegistryEntry[]>(
		`/api/maps/${encodeURIComponent(workspace.val.slug)}/registries/${encodeURIComponent(slug)}/documents`,
	)
	if (r.ok) {
		store.registry_entries = r.data
	}
	store.registry_entries_loading = false
}

async function load_maps() {
	const r = await api_get<MapSummary[]>("/api/maps")
	if (r.ok) {
		store.maps = r.data
	}
}

async function load_doc_links(doc_id: string) {
	store.doc_links_loading = true
	store.doc_links = []
	const r = await api_get<LinkInfo[]>(`/api/links/${doc_id}`)
	if (r.ok) {
		store.doc_links = r.data
	}
	store.doc_links_loading = false
}

async function load_doc_history(doc_id: string) {
	store.doc_history_loading = true
	store.doc_history = []
	const r = await api_get<DocumentVersionInfo[]>(`/api/documents/${doc_id}/history`)
	if (r.ok) {
		store.doc_history = r.data
	}
	store.doc_history_loading = false
}

/** Intră în modul rectificare: GenerateForm preia șablonul + datele documentului. */
async function start_rectification(doc: DocumentSummary) {
	const detail = await api_get<DocumentDetail>(`/api/documents/${doc.id}`)
	if (!detail.ok) {
		show_toast(`Eroare: ${detail.err?.message ?? ""}`, "err")
		return
	}
	const tpl = store.templates.find((x) => x.id === doc.template_id)
	if (!tpl) {
		show_toast("Șablonul documentului nu mai există — nu se poate rectifica.", "err")
		return
	}
	gen_inputs = {}
	store.gen_template = tpl.slug
	store.regen_doc_id = doc.id
	store.regen_doc_label = doc.registry_code || doc.name
	store.regen_data = detail.data.data ?? {}
	navigate("/documents")
	window.scrollTo(0, 0)
}

function cancel_rectification() {
	store.regen_doc_id = ""
	store.regen_doc_label = ""
	store.regen_data = null
	gen_inputs = {}
}

// ─────────────────────────────────────────────
// Utilitare
// ─────────────────────────────────────────────

function fmt_size(bytes: number): string {
	if (bytes < 1024) {
		return `${bytes} B`
	}
	if (bytes < 1024 * 1024) {
		return `${(bytes / 1024).toFixed(1)} KB`
	}
	return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

function fmt_date(iso: string): string {
	const d = new Date(iso)
	return isNaN(d.getTime()) ? iso : d.toLocaleDateString("ro-RO", { dateStyle: "medium" })
}

/** Identificator URL-safe generat din nume (transliterare diacritice RO). */
function slugify(s: string): string {
	return s
		.replace(/[ăĂâÂ]/g, "a")
		.replace(/[îÎ]/g, "i")
		.replace(/[șşȘŞ]/g, "s")
		.replace(/[țţȚŢ]/g, "t")
		.toLowerCase()
		.replace(/[\s—-]+/g, "_")
		.replace(/[^a-z0-9_]/g, "")
		.replace(/_+/g, "_")
		.replace(/^_+|_+$/g, "")
}

/** Valoarea unui câmp din contextul entității: source explicit, altfel flat[key]. */
function context_value(ctx: EntityContext, f: TemplateField): unknown {
	if (f.source) {
		let current: unknown = ctx
		for (const segment of f.source.split(".")) {
			if (current === null || typeof current !== "object") {
				return undefined
			}
			current = (current as Record<string, unknown>)[segment]
		}
		return current
	}
	return ctx.flat[f.key]
}

// ─────────────────────────────────────────────
// Stiluri partajate
// ─────────────────────────────────────────────

const INPUT_CLS =
	"w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900 " +
	"placeholder:text-slate-400 focus:outline-none focus:border-cyan-600 focus:ring-2 " +
	"focus:ring-cyan-600/20 transition"

const TEXTAREA_CLS =
	"w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-xs text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900 " +
	"placeholder:text-slate-400 focus:outline-none focus:border-cyan-600 focus:ring-2 " +
	"focus:ring-cyan-600/20 transition resize-y font-mono"

const SELECT_CLS =
	"w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900 " +
	"focus:outline-none focus:border-cyan-600 focus:ring-2 focus:ring-cyan-600/20 transition"

const LABEL_CLS =
	"block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1"

const BTN_PRIMARY =
	"inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-cyan-700 text-white text-sm " +
	"font-semibold hover:bg-cyan-800 disabled:opacity-50 disabled:cursor-not-allowed transition"

// max-md:py-2 — țintă touch mai înaltă doar pe telefon (cele 9 butoane-iconiță per document)
const BTN_SM =
	"inline-flex items-center gap-1 px-3 py-1.5 max-md:py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs " +
	"font-semibold hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed transition"

const BTN_SM_DANGER =
	"inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 text-xs " +
	"font-semibold hover:bg-red-100 transition"

const FILE_CLS =
	"block w-full text-sm text-slate-600 dark:text-slate-300 file:mr-3 file:px-3 file:py-1.5 file:rounded-lg " +
	"file:border-0 file:bg-cyan-50 file:text-cyan-700 file:text-xs file:font-semibold " +
	"hover:file:bg-cyan-100 file:cursor-pointer"

const CHIP_CLS =
	"inline-block px-2 py-0.5 rounded-md bg-violet-50 text-violet-700 text-[11px] font-mono"

function Field(label: string, input: HTMLElement) {
	return t.div({ class: "flex flex-col" }, t.label({ class: LABEL_CLS }, label), input)
}

// ─────────────────────────────────────────────
// Selector link GIS (hartă din listă sau UUID liber)
// ─────────────────────────────────────────────

/** Încarcă numele entității legate (pentru chip) din contextul ei. */
/** Eticheta cipului „Doar documentele entității …": un obiect fără etichetă are drept „nume" chiar uuid-ul → forma scurtă, plus stratul. */
function entity_chip_label(id: string, name: string, layer_name?: string): string {
	const short = name && !/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(name) ? name : id.slice(0, 8)
	return `${short}${layer_name ? ` · ${layer_name}` : ""}`
}

async function load_gen_entity(id: string) {
	if (!id) {
		store.gen_entity = { id: "", name: "", layer_name: "" }
		return
	}
	const r = await api_get<EntityContext>(`/api/entities/${id}/context`)
	if (r.ok) {
		store.gen_entity = {
			id,
			name: r.data.entity.name || id.slice(0, 8),
			layer_name: r.data.layer?.name ?? "",
		}
		if (store.filter_entity_id === id) {
			store.filter_entity_label = entity_chip_label(
				id,
				r.data.entity.name || "",
				r.data.layer?.name,
			)
		}
	} else {
		store.gen_entity = { id: "", name: "", layer_name: "" }
		if (store.filter_entity_id === id) {
			// Venit din popup-ul unui act al cărui obiect a fost ȘTERS de pe hartă (O-C-7): actele lui
			// rămân, iar lista le arată — nu e o eroare, doar nu se mai poate genera pe acel obiect.
			store.filter_entity_label = `${id.slice(0, 8)} · obiectul nu mai e pe hartă`
			show_toast("Obiectul nu mai există pe hartă — lista arată actele emise pe el.", "info")
		} else {
			show_toast(`Entitatea legată nu există: ${r.err?.message ?? ""}`, "err")
		}
	}
}

/** Chip read-only cu entitatea legată — selecția se face PE HARTĂ, nu aici. */
function EntityChip() {
	return t.div({ class: "flex items-center min-h-[2.4rem]" }, () => {
		const e = store.gen_entity
		if (!e.id) {
			return t.p(
				{ class: "text-xs text-slate-400 dark:text-slate-500" },
				"— nicio entitate — ",
				t.a(
					{
						class: "font-semibold text-cyan-700 dark:text-cyan-300 hover:text-cyan-900 underline",
						href: "#/map",
					},
					"alege parcela de pe hartă →",
				),
			)
		}
		return t.span(
			{
				class:
					"inline-flex items-center gap-2 rounded-lg border border-cyan-200 bg-cyan-50 dark:bg-cyan-950 px-3 py-1.5 text-xs font-semibold text-cyan-900",
			},
			"📍 ",
			e.name,
			e.layer_name
				? t.span({ class: "text-cyan-700 dark:text-cyan-300 font-normal" }, `· ${e.layer_name}`)
				: t.span(),
			t.button(
				{
					class: "text-cyan-700 dark:text-cyan-300 hover:text-red-600 font-bold ml-1",
					title: "Deselectează entitatea",
					onclick: () => {
						store.gen_entity = { id: "", name: "", layer_name: "" }
					},
				},
				"✕",
			),
		)
	})
}

// ─────────────────────────────────────────────
// Tab Șabloane
// ─────────────────────────────────────────────

function TemplateUploadForm() {
	let slug_touched = false
	const file_input = t.input({ type: "file", accept: ".docx", class: FILE_CLS })
	const slug_input = t.input({
		type: "text",
		class: INPUT_CLS,
		placeholder: "se generează din nume",
		oninput: () => {
			slug_touched = true
		},
	})
	const name_input = t.input({
		type: "text",
		class: INPUT_CLS,
		placeholder: "ex: Memoriu general PUG",
		oninput: (e: Event) => {
			if (!slug_touched) {
				slug_input.value = slugify((e.target as HTMLInputElement).value)
			}
		},
	})
	const desc_input = t.input({ type: "text", class: INPUT_CLS, placeholder: "opțional" })
	// Scope: Global (sistem, doar super) sau o organizație în care ești editor+.
	const scope_opts = van.state<Array<{ value: string; label: string }>>([])
	const scope_select = t.select({ class: INPUT_CLS })
	van.derive(() => {
		scope_select.replaceChildren(
			...scope_opts.val.map((o) => t.option({ value: o.value }, o.label)),
		)
	})
	template_scope_options().then((opts) => {
		scope_opts.val = opts
	})

	async function submit() {
		const file = file_input.files?.[0]
		const slug = slug_input.value.trim() || slugify(name_input.value)
		if (!file || !slug || !name_input.value.trim()) {
			show_toast("Completează fișierul și numele.", "err")
			return
		}
		const fd = new FormData()
		fd.append("file", file)
		fd.append("slug", slug)
		fd.append("name", name_input.value.trim())
		if (desc_input.value.trim()) {
			fd.append("description", desc_input.value.trim())
		}
		// value="" = global; trimitem `org` doar când e o organizație aleasă.
		if (scope_select.value) {
			fd.append("org", scope_select.value)
		}
		const r = await api_upload<{ slug: string }>("/api/templates", fd)
		if (r.ok) {
			show_toast(`Șablon '${r.data.slug}' încărcat.`, "ok")
			file_input.value = ""
			slug_input.value = ""
			name_input.value = ""
			desc_input.value = ""
			slug_touched = false
			await load_templates()
		} else {
			show_toast(`Eroare: ${r.err?.message ?? ""}`, "err")
		}
	}

	return t.section(
		{
			class:
				"bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-5",
		},
		t.h2(
			{ class: "text-sm font-bold text-slate-700 dark:text-slate-200 mb-3" },
			"Încarcă șablon nou (.docx)",
		),
		t.p(
			{ class: "text-xs text-slate-500 dark:text-slate-400 mb-4" },
			"Folosește acolade simple în Word: ",
			t.code({ class: CHIP_CLS }, "{camp}"),
			" pentru valori, ",
			t.code({ class: CHIP_CLS }, "{FOR p IN lista} {$p.camp} {END-FOR p}"),
			" pentru liste.",
		),
		t.div(
			{ class: "grid sm:grid-cols-2 gap-3" },
			Field("Fișier", file_input),
			Field("Nume", name_input),
			Field("Vizibilitate", scope_select),
			Field("Descriere", desc_input),
		),
		t.details(
			{ class: "mt-3" },
			t.summary(
				{
					class:
						"text-xs font-semibold text-slate-500 dark:text-slate-400 cursor-pointer hover:text-slate-700",
				},
				"Setări avansate",
			),
			t.div(
				{ class: "grid sm:grid-cols-2 gap-3 mt-3" },
				Field("Identificator (se generează din nume)", slug_input),
			),
		),
		t.div(
			{ class: "mt-4" },
			t.button({ class: BTN_PRIMARY, onclick: submit }, icon("upload-simple"), "Încarcă șablonul"),
		),
	)
}

function TemplateCard(tpl: TemplateSummary) {
	const replace_input = t.input({ type: "file", accept: ".docx", class: FILE_CLS })

	async function replace_file() {
		const file = replace_input.files?.[0]
		if (!file) {
			show_toast("Alege fișierul .docx nou.", "err")
			return
		}
		const fd = new FormData()
		fd.append("file", file)
		const r = await api_upload<{ version: number }>(`/api/templates/${tpl.slug}/file`, fd)
		if (r.ok) {
			show_toast(`Șablon '${tpl.slug}' actualizat (v${r.data.version}).`, "ok")
			store.replacing_template = ""
			await load_templates()
		} else {
			show_toast(`Eroare: ${r.err?.message ?? ""}`, "err")
		}
	}

	async function remove() {
		if (!window.confirm(`Ștergi șablonul '${tpl.name || tpl.slug}'? Documentele generate rămân.`)) {
			return
		}
		const r = await api_delete(`/api/templates/${tpl.slug}`)
		if (r.ok) {
			show_toast("Șablon șters.", "ok")
			await load_templates()
		} else {
			show_toast(`Eroare: ${r.err?.message ?? ""}`, "err")
		}
	}

	return t.div(
		{
			class:
				"bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-5",
		},
		t.div(
			{ class: "flex items-start justify-between gap-3 flex-wrap" },
			t.div(
				t.div(
					{ class: "flex items-center gap-2 flex-wrap" },
					t.h3(
						{ class: "text-sm font-bold text-slate-800 dark:text-slate-200" },
						tpl.name || tpl.slug,
					),
					tpl.org_id
						? t.span(
								{
									class:
										"inline-flex items-center rounded-full bg-cyan-100 text-cyan-800 dark:text-cyan-300 px-2 py-0.5 text-[10px] font-bold",
								},
								// numele primăriei, nu un „Organizație" generic — super vede toate șabloanele
								(
									(org_id) => () =>
										org_name_of(org_id)
								)(tpl.org_id),
							)
						: t.span(
								{
									class:
										"inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-950 text-amber-800 dark:text-amber-300 px-2 py-0.5 text-[10px] font-bold",
								},
								"Global",
							),
				),
				t.p(
					{ class: "text-xs text-slate-500 dark:text-slate-400 mt-0.5" },
					`${tpl.slug} · v${tpl.version} · ${fmt_size(tpl.size)} · ${fmt_date(tpl.created_at)}`,
				),
				tpl.description
					? t.p({ class: "text-xs text-slate-600 dark:text-slate-300 mt-1" }, tpl.description)
					: t.span(),
			),
			t.div(
				{ class: "flex items-center gap-2 flex-wrap" },
				t.button(
					{
						class: BTN_SM,
						onclick: () => {
							store.gen_template = tpl.slug
							gen_inputs = {}
							navigate("/documents")
						},
					},
					"Generează →",
				),
				// Scrierea (câmpuri / fișier / ștergere) doar unde serverul o permite (M1-O1): butoanele
				// sunt montate STATIC și comutate prin style (regula Van; ⚠️ `hidden` n-ar bate
				// `inline-flex` din BTN_SM în ordinea CSS-ului Tailwind), decizia e reactivă pe sesiune.
				t.button(
					{
						class: BTN_SM,
						style: () => (can_manage_template(tpl) ? "" : "display:none"),
						onclick: () => {
							store.editing_fields_template =
								store.editing_fields_template === tpl.slug ? "" : tpl.slug
						},
					},
					"Câmpuri",
				),
				t.a({ class: BTN_SM, href: `/api/templates/${tpl.slug}/file` }, "Descarcă"),
				t.button(
					{
						class: BTN_SM,
						style: () => (can_manage_template(tpl) ? "" : "display:none"),
						onclick: () => {
							store.replacing_template = store.replacing_template === tpl.slug ? "" : tpl.slug
						},
					},
					"Înlocuiește fișier",
				),
				t.button(
					{
						class: BTN_SM_DANGER,
						style: () => (can_manage_template(tpl) ? "" : "display:none"),
						onclick: remove,
					},
					"Șterge",
				),
				t.span(
					{
						class: "text-xs text-slate-400 dark:text-slate-500 italic",
						style: () => (can_manage_template(tpl) ? "display:none" : ""),
					},
					() => readonly_reason(tpl),
				),
			),
		),
		t.div(
			{ class: "mt-3 flex flex-wrap gap-1.5" },
			...tpl.fields.map((f) =>
				t.span(
					{
						class:
							f.type === "list"
								? CHIP_CLS + " bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-300"
								: CHIP_CLS,
					},
					`{${f.key}}${f.type && f.type !== "string" ? `:${f.type}` : ""}`,
				),
			),
			tpl.fields.length === 0
				? t.span(
						{ class: "text-xs text-slate-400 dark:text-slate-500" },
						"fără placeholders detectate",
					)
				: t.span(),
		),
		t.div(
			{
				class: "mt-3 flex items-end gap-2",
				style: () => `display:${store.replacing_template === tpl.slug ? "flex" : "none"}`,
			},
			t.div({ class: "flex-1" }, replace_input),
			t.button({ class: BTN_SM, onclick: replace_file }, "Încarcă versiune nouă"),
		),
		t.div(
			{ style: () => `display:${store.editing_fields_template === tpl.slug ? "block" : "none"}` },
			FieldsEditor(tpl),
		),
	)
}

// ─────────────────────────────────────────────
// Editor de câmpuri (schema formularului de completare + legături autofill)
// ─────────────────────────────────────────────

function FieldsEditor(tpl: TemplateSummary) {
	// Copie editabilă locală; salvarea trimite întreaga listă prin PATCH.
	const draft: TemplateField[] = tpl.fields.map((f) => ({ ...f }))

	async function save() {
		if (!can_manage_template(tpl)) {
			show_toast(readonly_reason(tpl), "err")
			return
		}
		const r = await api_patch(`/api/templates/${tpl.slug}`, { fields: draft })
		if (r.ok) {
			show_toast("Câmpuri salvate.", "ok")
			store.editing_fields_template = ""
			await load_templates()
		} else {
			show_toast(`Eroare: ${r.err?.message ?? ""}`, "err")
		}
	}

	const type_options: TemplateFieldType[] = ["string", "number", "boolean", "date", "list"]

	function row(f: TemplateField) {
		return t.div(
			{ class: "grid sm:grid-cols-[1fr_1fr_8rem_1fr_1fr_4rem] gap-2 items-center" },
			t.span({ class: CHIP_CLS + " justify-self-start" }, `{${f.key}}`),
			t.input({
				type: "text",
				class: INPUT_CLS,
				value: f.label ?? "",
				placeholder: "etichetă",
				oninput: (e: Event) => {
					f.label = (e.target as HTMLInputElement).value || undefined
				},
			}),
			t.select(
				{
					class: SELECT_CLS,
					onchange: (e: Event) => {
						f.type = (e.target as HTMLSelectElement).value as TemplateFieldType
					},
				},
				...type_options.map((ty) =>
					t.option({ value: ty, selected: (f.type ?? "string") === ty }, ty),
				),
			),
			t.input({
				type: "text",
				class: INPUT_CLS,
				value: f.source ?? "",
				placeholder: "sursă autofill (ex: attributes.suprafata)",
				oninput: (e: Event) => {
					f.source = (e.target as HTMLInputElement).value || undefined
				},
			}),
			t.input({
				type: "text",
				class: INPUT_CLS,
				value: f.default !== undefined && f.default !== null ? String(f.default) : "",
				placeholder: "valoare implicită",
				oninput: (e: Event) => {
					const v = (e.target as HTMLInputElement).value
					f.default = v === "" ? undefined : v
				},
			}),
			t.label(
				{
					class:
						"flex items-center gap-1 text-[11px] text-slate-600 dark:text-slate-300 justify-self-center",
				},
				t.input({
					type: "checkbox",
					class: "accent-cyan-700",
					checked: f.required !== false,
					onchange: (e: Event) => {
						f.required = (e.target as HTMLInputElement).checked
					},
				}),
				"oblig.",
			),
		)
	}

	return t.div(
		{ class: "mt-3 border-t border-slate-100 dark:border-slate-800 pt-3" },
		t.div(
			{ class: "hidden sm:grid sm:grid-cols-[1fr_1fr_8rem_1fr_1fr_4rem] gap-2 mb-1" },
			...["cheie", "etichetă", "tip", "sursă autofill", "implicit", ""].map((h) =>
				t.span(
					{
						class:
							"text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wide",
					},
					h,
				),
			),
		),
		t.div({ class: "flex flex-col gap-2" }, ...draft.map((f) => row(f))),
		t.p(
			{ class: "text-[11px] text-slate-400 dark:text-slate-500 mt-2" },
			"Surse autofill: attributes.<cheie> (atribut EAV), entity.name, map.name, layer.name — sau gol pentru potrivire pe nume de cheie.",
		),
		t.div(
			{ class: "mt-3 flex items-center gap-3 flex-wrap" },
			t.button(
				{ class: BTN_SM, disabled: () => !can_manage_template(tpl), onclick: save },
				"Salvează câmpurile",
			),
			t.span(
				{
					class: "text-xs text-slate-400 dark:text-slate-500 italic",
					style: () => (can_manage_template(tpl) ? "display:none" : ""),
				},
				() => readonly_reason(tpl),
			),
		),
	)
}

/**
 * Cardurile grupate pe proprietar (M1-O2): „Global (sistem)" primul, apoi primăriile alfabetic.
 * Cu un singur proprietar lista rămâne plată. Citește numele reactiv (store.org_names / sesiune).
 */
function template_groups(
	list: TemplateSummary[],
): Array<{ key: string; label: string; items: TemplateSummary[] }> {
	const by = new Map<string, TemplateSummary[]>()
	for (const tpl of list) {
		const key = tpl.org_id ?? ""
		const bucket = by.get(key)
		if (bucket) {
			bucket.push(tpl)
		} else {
			by.set(key, [tpl])
		}
	}
	return [...by.entries()]
		.map(([key, items]) => ({
			key,
			label: key === "" ? "Global (sistem)" : org_name_of(key),
			items,
		}))
		.toSorted((x, y) =>
			x.key === "" ? -1 : y.key === "" ? 1 : x.label.localeCompare(y.label, "ro"),
		)
}

function TemplatesTab() {
	return t.div(
		{
			class: "flex flex-col gap-4",
			style: () => `display:${store.active_tab === "templates" ? "flex" : "none"}`,
		},
		TemplateUploadForm(),
		t.div({ class: "flex flex-col gap-3" }, () =>
			t.div(
				{ class: "flex flex-col gap-3" },
				store.templates_loading
					? t.p({ class: "text-sm text-slate-500 dark:text-slate-400" }, "Se încarcă…")
					: t.span(),
				...(() => {
					// super/support: numele primăriilor se cer o dată, DUPĂ hidratare (gardă pe stare)
					if (session_loaded.val && org_names_state === "idle") {
						void load_org_names()
					}
					const groups = template_groups(store.templates)
					if (groups.length < 2) {
						return store.templates.map((tpl) => TemplateCard(tpl))
					}
					const rows: HTMLElement[] = []
					for (const g of groups) {
						rows.push(
							t.h3(
								{
									class:
										"text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 mt-2",
								},
								g.label,
							),
						)
						for (const tpl of g.items) {
							rows.push(TemplateCard(tpl))
						}
					}
					return rows
				})(),
				!store.templates_loading && store.templates.length === 0
					? t.p(
							{ class: "text-sm text-slate-500 dark:text-slate-400" },
							"Niciun șablon încă — încarcă primul mai sus.",
						)
					: t.span(),
			),
		),
	)
}

// ─────────────────────────────────────────────
// Tab Documente: generare
// ─────────────────────────────────────────────

/**
 * `<textarea>` pentru o valoare de text pe mai multe rânduri. Înălțimea e estimată din text (un rând
 * lung se rupe pe mai multe în coloana îngustă a formularului — ~32 de caractere pe rând), plafonată;
 * utilizatorul o poate trage mai mare.
 */
function multiline_input(value: string, placeholder: string): HTMLTextAreaElement {
	const visual_rows = value
		.split("\n")
		.reduce((n, line) => n + Math.max(1, Math.ceil(line.length / 32)), 0)
	return t.textarea({
		class: INPUT_CLS + " resize-y",
		rows: String(Math.min(14, visual_rows + 1)),
		placeholder,
	}) as HTMLTextAreaElement
}

/**
 * Scrie o valoare într-un câmp al formularului de generare. Când valoarea are mai multe rânduri și
 * câmpul e un `<input type="text">`, îl ÎNLOCUIEȘTE cu un `<textarea>` (altfel browserul ar șterge
 * sfârșiturile de rând și rubricile „Etichetă: text" ar ajunge lipite în act).
 */
function set_gen_value(key: string, value: string): void {
	const el = gen_inputs[key]
	if (!el) {
		return
	}
	if (value.includes("\n") && el instanceof HTMLInputElement && el.type === "text") {
		const ta = multiline_input(value, el.placeholder)
		el.replaceWith(ta)
		gen_inputs[key] = ta
		ta.value = value
		return
	}
	el.value = value
}

/** Input potrivit tipului de câmp; se înregistrează în gen_inputs[f.key]. */
function field_input(f: TemplateField): HTMLElement {
	const placeholder =
		f.default !== undefined && f.default !== null
			? `implicit: ${String(f.default)}`
			: (f.description ?? "")
	let el: HTMLInputElement | HTMLTextAreaElement
	switch (f.type ?? "string") {
		case "number":
			el = t.input({ type: "number", step: "any", class: INPUT_CLS, placeholder })
			break
		case "date":
			el = t.input({ type: "date", class: INPUT_CLS })
			break
		case "boolean":
			el = t.input({ type: "checkbox", class: "accent-cyan-700 h-5 w-5 mt-2" })
			break
		case "list":
			el = t.textarea({
				class: TEXTAREA_CLS,
				rows: 3,
				placeholder: placeholder || `[{"nr": 1, "denumire": "…"}]`,
			})
			break
		default: {
			// Un câmp de text cu mai multe RÂNDURI (regimul economic / tehnic, compuse din fișa RLU) are
			// nevoie de `<textarea>`: un `<input type="text">` șterge sfârșiturile de rând la atribuirea
			// valorii, iar rubricile ar pleca lipite spre act.
			const seed_text = store.regen_doc_id ? store.regen_data?.[f.key] : undefined
			el =
				typeof seed_text === "string" && seed_text.includes("\n")
					? multiline_input(seed_text, placeholder)
					: t.input({ type: "text", class: INPUT_CLS, placeholder })
		}
	}
	// Rectificare: pre-completează cu datele documentului existent.
	const seed = store.regen_doc_id ? store.regen_data?.[f.key] : undefined
	if (seed !== undefined && seed !== null) {
		if ((f.type ?? "string") === "boolean") {
			;(el as HTMLInputElement).checked = seed === true
		} else {
			el.value = typeof seed === "object" ? JSON.stringify(seed) : String(seed)
		}
	}
	gen_inputs[f.key] = el
	return el
}

/** Citește valorile tastate, coerce pe tipul câmpului. Întoarce null la eroare (cu toast). */
function collect_gen_data(fields: TemplateField[]): Record<string, unknown> | null {
	const data: Record<string, unknown> = {}
	for (const f of fields) {
		const el = gen_inputs[f.key]
		if (!el) {
			continue
		}
		if ((f.type ?? "string") === "boolean") {
			data[f.key] = (el as HTMLInputElement).checked
			continue
		}
		const raw = el.value.trim()
		if (raw === "") {
			continue // lipsă => decide serverul (default/autofill/required)
		}
		switch (f.type ?? "string") {
			case "number": {
				const n = Number(raw)
				if (isNaN(n)) {
					show_toast(`'${f.label ?? f.key}' trebuie să fie număr.`, "err")
					return null
				}
				data[f.key] = n
				break
			}
			case "list":
				try {
					data[f.key] = JSON.parse(raw)
				} catch {
					show_toast(`'${f.label ?? f.key}' nu e JSON valid (ex: [{"nr":1}]).`, "err")
					return null
				}
				break
			default:
				data[f.key] = raw
		}
	}
	return data
}

function GenerateForm() {
	const name_input = t.input({
		type: "text",
		class: INPUT_CLS,
		placeholder: "implicit: nume șablon + data",
	})
	const allow_missing = t.input({ type: "checkbox", class: "accent-cyan-700" })

	async function autofill() {
		const tpl = store.templates.find((x) => x.slug === store.gen_template)
		if (!tpl) {
			show_toast("Alege un șablon mai întâi.", "err")
			return
		}
		// Fără entitate (M3-O1): contextul e al HĂRȚII din workspace — rubricile de emitent (județ,
		// autoritate, semnături) se completează și pentru un act care nu ține de o parcelă anume.
		// Toastul rămâne doar când nu există nici hartă selectată.
		let context_id = store.gen_entity.id
		const from_map = !context_id
		if (from_map) {
			if (!workspace.val.slug) {
				show_toast(
					"Alege întâi o hartă de lucru sau parcela de pe hartă (Hartă → click pe parcelă → Generează).",
					"err",
				)
				return
			}
			let map = store.maps.find((m) => m.slug === workspace.val.slug)
			if (!map) {
				await load_maps()
				map = store.maps.find((m) => m.slug === workspace.val.slug)
			}
			if (!map) {
				show_toast("Harta de lucru nu a fost găsită — alege-o din nou.", "err")
				return
			}
			context_id = map.id
		}
		const r = await api_get<EntityContext>(`/api/entities/${context_id}/context`)
		if (!r.ok) {
			show_toast(`Eroare context: ${r.err?.message ?? ""}`, "err")
			return
		}
		let filled = 0
		for (const f of tpl.fields) {
			const el = gen_inputs[f.key]
			if (!el || (f.type ?? "string") === "boolean") {
				continue
			}
			if (el.value.trim() !== "") {
				continue // doar câmpurile goale
			}
			const v = context_value(r.data, f)
			if (v === undefined || v === null) {
				continue
			}
			set_gen_value(f.key, typeof v === "object" ? JSON.stringify(v) : String(v))
			filled++
		}
		show_toast(
			filled > 0
				? from_map
					? `${filled} câmpuri completate din datele hărții '${r.data.entity.name}'.`
					: `${filled} câmpuri completate din '${r.data.entity.name}'.`
				: from_map
					? "Nimic de completat din datele hărții — completează datele de emitent în Admin → Straturi sau alege o parcelă de pe hartă."
					: "Nimic de completat — fără potriviri în contextul entității.",
			filled > 0 ? "ok" : "info",
		)
	}

	async function submit() {
		const tpl = store.templates.find((x) => x.slug === store.gen_template)
		if (!tpl) {
			show_toast("Alege un șablon.", "err")
			return
		}
		const data = collect_gen_data(tpl.fields)
		if (data === null) {
			return
		}
		store.gen_loading = true

		// Rectificare: versiune nouă cu ACELAȘI număr de registru.
		if (store.regen_doc_id) {
			const r = await api_post<DocumentSummary>(`/api/documents/${store.regen_doc_id}/regenerate`, {
				data,
				allow_missing: allow_missing.checked,
			})
			store.gen_loading = false
			if (r.ok) {
				store.last_generated = r.data
				show_toast(`Document rectificat — păstrează numărul ${r.data.registry_code || ""}.`, "ok")
				cancel_rectification()
				await load_documents()
			} else {
				show_toast(`Eroare: ${r.err?.message ?? ""}`, "err")
			}
			return
		}

		const r = await api_post<DocumentSummary>(`/api/templates/${tpl.slug}/generate`, {
			data,
			name: name_input.value.trim() || null,
			entity_id: store.gen_entity.id || null,
			map: workspace.val.slug || null,
			registry: store.gen_registry || null,
			allow_missing: allow_missing.checked,
		})
		store.gen_loading = false
		if (r.ok) {
			store.last_generated = r.data
			show_toast(
				r.data.registry_code
					? `Document '${r.data.name}' generat — număr de registru ${r.data.registry_code}.`
					: `Document '${r.data.name}' generat.`,
				"ok",
			)
			await load_documents()
			if (store.gen_registry) {
				await load_registries() // count + ultimul cod s-au schimbat
			}
		} else {
			show_toast(`Eroare: ${r.err?.message ?? ""}`, "err")
		}
	}

	return t.section(
		{
			class:
				"bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-5",
		},
		t.h2({ class: "text-sm font-bold text-slate-700 dark:text-slate-200 mb-3" }, () =>
			store.regen_doc_id ? "Rectificare document" : "Generează document din șablon",
		),
		// Banner de rectificare: păstrează numărul, doar datele se corectează.
		t.div(
			{
				class:
					"mb-3 flex items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950 px-3 py-2",
				style: () => `display:${store.regen_doc_id ? "flex" : "none"}`,
			},
			t.p(
				{ class: "text-xs font-semibold text-amber-800 dark:text-amber-300" },
				() =>
					`Rectificare pentru ${store.regen_doc_label} — corectează câmpurile; documentul păstrează numărul de registru și primește o versiune nouă.`,
			),
			t.button(
				{
					class: "text-xs font-bold text-amber-700 dark:text-amber-300 hover:text-amber-900",
					onclick: cancel_rectification,
				},
				"✕ renunță",
			),
		),
		t.div(
			{
				class: "grid sm:grid-cols-2 gap-3",
				// În rectificare șablonul/numele/registrul/entitatea sunt fixate de documentul țintă.
				style: () => `display:${store.regen_doc_id ? "none" : "grid"}`,
			},
			Field(
				"Șablon",
				t.select(
					{
						class: SELECT_CLS,
						onchange: (e: Event) => {
							gen_inputs = {}
							store.gen_template = (e.target as HTMLSelectElement).value
						},
					},
					() =>
						t.optgroup(
							{ label: "Șabloane" },
							t.option({ value: "", selected: () => store.gen_template === "" }, "— alege —"),
							...store.templates.map((m) =>
								t.option(
									{ value: m.slug, selected: () => store.gen_template === m.slug },
									m.name || m.slug,
								),
							),
						),
				),
			),
			Field("Nume document (opțional)", name_input),
			Field(
				"Registru (numărul următor, opțional)",
				t.div(
					{ class: "flex flex-col gap-1" },
					t.select(
						{
							class: SELECT_CLS,
							onchange: (e: Event) => {
								store.gen_registry = (e.target as HTMLSelectElement).value
							},
						},
						() =>
							t.optgroup(
								{ label: `Registrele hărții ${workspace.val.name || "—"}` },
								t.option(
									{ value: "", selected: () => store.gen_registry === "" },
									"— fără număr de registru —",
								),
								...store.registries.map((rg) =>
									t.option(
										{ value: rg.slug, selected: () => store.gen_registry === rg.slug },
										`${rg.name || rg.slug} (urmează ${rg.prefix}…${rg.suffix})`,
									),
								),
							),
					),
					() => {
						const rg = store.registries.find((x) => x.slug === store.gen_registry)
						return t.p(
							{
								class:
									"text-[11px] " +
									(rg && !rg.number_field
										? "font-medium text-amber-700 dark:text-amber-300"
										: "text-slate-400 dark:text-slate-500"),
							},
							rg
								? rg.number_field
									? `Documentul primește automat numărul următor în câmpul {${rg.number_field}}; ultimul: ${rg.last_code || "—"}.`
									: `Registrul nu are câmp de șablon: ${NO_NUMBER_FIELD_NOTE} (se completează din pagina Registre); ultimul: ${rg.last_code || "—"}.`
								: "",
						)
					},
				),
			),
			Field("Parcela / entitatea legată (aleasă de pe hartă)", EntityChip()),
			t.div(
				{ class: "flex items-end gap-4 pb-2 flex-wrap" },
				t.label(
					{ class: "flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300" },
					allow_missing,
					"permite câmpuri lipsă (rămân goale)",
				),
			),
		),
		// Descrierea șablonului selectat — utilizatorul vede ce emite.
		() => {
			const tpl = store.templates.find((x) => x.slug === store.gen_template)
			return tpl?.description
				? t.p(
						{
							class:
								"mt-2 text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-950 rounded-lg px-3 py-2",
						},
						tpl.description,
					)
				: t.span()
		},
		// Câmpurile șablonului selectat (din schema fields) — re-randate la schimbarea selecției.
		() => {
			const tpl = store.templates.find((x) => x.slug === store.gen_template)
			if (!tpl) {
				return t.div(
					{ class: "mt-3 text-xs text-slate-400 dark:text-slate-500" },
					"Selectează un șablon pentru câmpuri.",
				)
			}
			gen_inputs = {}
			const scalar_fields = tpl.fields.filter((f) => (f.type ?? "string") !== "list")
			const list_fields = tpl.fields.filter((f) => f.type === "list")
			return t.div(
				{ class: "mt-4 border-t border-slate-100 dark:border-slate-800 pt-4" },
				t.div(
					{ class: "flex items-center justify-between mb-3" },
					t.h3(
						{
							class: "text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide",
						},
						"Date document",
					),
					// eticheta e un copil reactiv PROPRIU: funcția exterioară nu citește `gen_entity`, deci
					// alegerea/renunțarea la parcelă nu reconstruiește câmpurile (s-ar pierde ce e tastat)
					t.button({ class: BTN_SM, onclick: autofill }, () =>
						store.gen_entity.id
							? "⚡ Auto-completează din entitate"
							: "⚡ Auto-completează din datele hărții",
					),
				),
				t.div(
					{ class: "grid sm:grid-cols-2 gap-3" },
					...scalar_fields.map((f) =>
						Field((f.label ?? f.key) + (f.required === false ? "" : " *"), field_input(f)),
					),
				),
				list_fields.length > 0
					? t.div(
							{ class: "grid gap-3 mt-3" },
							...list_fields.map((f) => Field(`${f.label ?? f.key} (JSON array)`, field_input(f))),
						)
					: t.span(),
			)
		},
		t.div(
			{ class: "mt-4" },
			t.button(
				{
					class: BTN_PRIMARY,
					disabled: () => store.gen_loading,
					onclick: submit,
				},
				icon("file-plus"),
				() =>
					store.gen_loading
						? " Se generează…"
						: store.regen_doc_id
							? " Rectifică documentul"
							: " Generează documentul",
			),
		),
		// Panou de succes: documentul proaspăt generat, cu descărcare proeminentă.
		() => {
			const d = store.last_generated
			if (!d) {
				return t.span()
			}
			return t.div(
				{
					class:
						"mt-3 flex items-center justify-between gap-3 flex-wrap rounded-lg border border-emerald-300 bg-emerald-50 dark:bg-emerald-950 px-4 py-3",
				},
				t.div(
					t.p({ class: "text-sm font-bold text-emerald-900" }, "✓ ", d.name || d.filename),
					d.registry_code
						? t.p(
								{ class: "text-xs text-emerald-800 dark:text-emerald-300 mt-0.5" },
								"Număr de registru: ",
								t.span({ class: "font-mono font-bold" }, d.registry_code),
							)
						: t.span(),
				),
				t.a(
					{
						class:
							"inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-700 text-white text-sm font-semibold hover:bg-emerald-800 transition",
						href: `/api/documents/${d.id}/file`,
					},
					"⬇ Descarcă documentul",
				),
			)
		},
	)
}

// ─────────────────────────────────────────────
// Tab Documente: înregistrare manuală
// ─────────────────────────────────────────────

function RegisterForm() {
	const registry_pick = { value: "" }
	const file_input = t.input({
		type: "file",
		accept: ".docx,.pdf,.png,.jpg,.jpeg",
		class: FILE_CLS,
	}) // serverul detectează tipul din magic bytes (sniff_registered_type)
	const name_input = t.input({
		type: "text",
		class: INPUT_CLS,
		placeholder: "ex: Aviz CJ Argeș nr. 12",
	})

	async function submit() {
		const file = file_input.files?.[0]
		if (!file || !name_input.value.trim()) {
			show_toast("Completează fișierul și numele.", "err")
			return
		}
		const fd = new FormData()
		fd.append("file", file)
		fd.append("name", name_input.value.trim())
		if (store.gen_entity.id) {
			fd.append("entity_id", store.gen_entity.id)
		}
		if (workspace.val.slug) {
			fd.append("map", workspace.val.slug)
		}
		if (registry_pick.value) {
			fd.append("registry", registry_pick.value)
		}
		const r = await api_upload<DocumentSummary>("/api/documents", fd)
		if (r.ok) {
			show_toast(
				r.data.registry_code
					? `Document '${r.data.name}' înregistrat — nr. ${r.data.registry_code}.`
					: `Document '${r.data.name}' înregistrat.`,
				"ok",
			)
			file_input.value = ""
			name_input.value = ""
			await load_documents()
			if (registry_pick.value) {
				await load_registries()
			}
		} else {
			show_toast(`Eroare: ${r.err?.message ?? ""}`, "err")
		}
	}

	return t.section(
		{
			class:
				"bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-5",
		},
		t.h2(
			{ class: "text-sm font-bold text-slate-700 dark:text-slate-200 mb-3" },
			"Înregistrează document existent (.docx)",
		),
		t.div(
			{ class: "grid sm:grid-cols-2 lg:grid-cols-4 gap-3" },
			Field("Fișier", file_input),
			Field("Nume", name_input),
			Field(
				"Registru (opțional)",
				t.select(
					{
						class: SELECT_CLS,
						onchange: (e: Event) => {
							registry_pick.value = (e.target as HTMLSelectElement).value
						},
					},
					() =>
						t.optgroup(
							{ label: `Registrele hărții ${workspace.val.name || "—"}` },
							t.option({ value: "" }, "— fără număr —"),
							...store.registries.map((rg) => t.option({ value: rg.slug }, rg.name || rg.slug)),
						),
				),
			),
			Field("Parcela / entitatea legată (opțional)", EntityChip()),
		),
		t.div(
			{ class: "mt-4" },
			t.button({ class: BTN_PRIMARY, onclick: submit }, icon("upload-simple"), "Înregistrează"),
		),
	)
}

// ─────────────────────────────────────────────
// Tab Documente: listă + legături
// ─────────────────────────────────────────────

function DocumentLinks(doc: DocumentSummary) {
	const rel_input = t.input({
		type: "text",
		class: INPUT_CLS,
		value: "related",
		placeholder: "related",
	})
	const other_select = t.select({ class: SELECT_CLS }, () =>
		t.optgroup(
			{ label: "Documente" },
			t.option({ value: "" }, "— alege documentul legat —"),
			...store.documents
				.filter((d) => d.id !== doc.id)
				.map((d) => t.option({ value: d.id }, d.name || d.filename)),
		),
	)

	async function add_link() {
		const dst = (other_select as HTMLSelectElement).value
		if (!dst) {
			show_toast("Alege documentul legat.", "err")
			return
		}
		const r = await api_post(`/api/links`, {
			src_id: doc.id,
			dst_id: dst,
			rel: rel_input.value.trim() || "related",
		})
		if (r.ok) {
			show_toast("Legătură creată.", "ok")
			await load_doc_links(doc.id)
		} else {
			show_toast(`Eroare: ${r.err?.message ?? ""}`, "err")
		}
	}

	async function unlink(link_id: string) {
		const r = await api_delete(`/api/links/${link_id}`)
		if (r.ok) {
			show_toast("Legătură ștearsă.", "ok")
			await load_doc_links(doc.id)
		} else {
			show_toast(`Eroare: ${r.err?.message ?? ""}`, "err")
		}
	}

	return t.div(
		{ class: "mt-3 border-t border-slate-100 dark:border-slate-800 pt-3" },
		t.h4(
			{
				class: "text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2",
			},
			"Legături",
		),
		() =>
			t.div(
				{ class: "flex flex-col gap-1.5" },
				store.doc_links_loading
					? t.p({ class: "text-xs text-slate-400 dark:text-slate-500" }, "Se încarcă…")
					: t.span(),
				...store.doc_links.map((l) =>
					t.div(
						{ class: "flex items-center gap-2 text-xs text-slate-700 dark:text-slate-200" },
						t.span(
							{
								class:
									CHIP_CLS + " bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300",
							},
							l.rel,
						),
						t.span({}, l.direction === "out" ? "→" : "←"),
						t.span(
							{ class: "font-medium" },
							l.other
								? `${l.other.name || l.other.id} (${l.other.kind ?? l.other.table})`
								: "obiect dispărut",
						),
						t.button(
							{
								class: "text-red-600 dark:text-red-400 hover:text-red-800 font-semibold ml-1",
								onclick: () => unlink(l.id),
							},
							"✕",
						),
					),
				),
				!store.doc_links_loading && store.doc_links.length === 0
					? t.p({ class: "text-xs text-slate-400 dark:text-slate-500" }, "Fără legături.")
					: t.span(),
			),
		t.div(
			{ class: "mt-2 grid sm:grid-cols-3 gap-2 items-end" },
			t.div({ class: "sm:col-span-2" }, other_select),
			t.div(
				{ class: "flex gap-2" },
				rel_input,
				t.button({ class: BTN_SM, onclick: add_link }, "Leagă"),
			),
		),
	)
}

function DocumentRow(doc: DocumentSummary) {
	const annulled = doc.status === "anulat"

	async function remove() {
		if (!window.confirm(`Ștergi documentul '${doc.name || doc.filename}'?`)) {
			return
		}
		const r = await api_delete(`/api/documents/${doc.id}`)
		if (r.ok) {
			show_toast("Document șters.", "ok")
			await load_documents()
		} else {
			show_toast(`Eroare: ${r.err?.message ?? ""}`, "err")
		}
	}

	async function annul() {
		const reason = window.prompt(
			`Anulezi documentul '${doc.registry_code || doc.name}'?\nNumărul rămâne ocupat în registru. Motivul anulării (opțional):`,
		)
		if (reason === null) {
			return // utilizatorul a renunțat
		}
		const r = await api_post<{ status: string }>(`/api/documents/${doc.id}/annul`, {
			reason: reason.trim() || null,
		})
		if (r.ok) {
			show_toast("Document anulat — numărul rămâne în registru.", "ok")
			await load_documents()
			await load_registries()
		} else {
			show_toast(`Eroare: ${r.err?.message ?? ""}`, "err")
		}
	}

	async function rename() {
		const name = window.prompt("Noul nume al documentului:", doc.name)
		if (!name || name.trim() === doc.name) {
			return
		}
		const r = await api_patch(`/api/documents/${doc.id}`, { name: name.trim() })
		if (r.ok) {
			show_toast("Document redenumit.", "ok")
			await load_documents()
		} else {
			show_toast(`Eroare: ${r.err?.message ?? ""}`, "err")
		}
	}

	function toggle_links() {
		if (store.expanded_doc === doc.id) {
			store.expanded_doc = ""
		} else {
			store.expanded_doc = doc.id
			store.doc_history_visible = ""
			load_doc_links(doc.id)
		}
	}

	function toggle_history() {
		if (store.doc_history_visible === doc.id) {
			store.doc_history_visible = ""
			return
		}
		store.expanded_doc = doc.id
		store.doc_history_visible = doc.id
		load_doc_links(doc.id)
		load_doc_history(doc.id)
	}

	return t.div(
		{
			id: `doc-${doc.id}`,
			class: () =>
				"bg-white dark:bg-slate-900 rounded-xl border p-3 " +
				(annulled
					? "border-red-200 dark:border-red-800"
					: "border-slate-200 dark:border-slate-700") +
				// actul venit din popup-ul hărții (#/documents?doc=<id>) — evidențiat
				(store.highlight_doc_id === doc.id ? " ring-2 ring-cyan-500" : ""),
		},
		t.div(
			{ class: "flex items-start justify-between gap-3 flex-wrap" },
			t.div(
				t.h3(
					{
						class:
							"text-sm font-bold " +
							(annulled
								? "text-slate-400 dark:text-slate-500 line-through"
								: "text-slate-800 dark:text-slate-200"),
					},
					doc.name || doc.filename,
				),
				t.p(
					{ class: "text-xs text-slate-500 dark:text-slate-400 mt-0.5" },
					`${doc.filename} · ${fmt_size(doc.size)} · ${fmt_date(doc.created_at)}`,
				),
			),
			t.div(
				{ class: "flex items-center gap-2 flex-wrap" },
				annulled
					? t.span(
							{
								class:
									"px-2 py-0.5 rounded-md bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 text-[11px] font-bold",
							},
							"ANULAT",
						)
					: t.span(),
				doc.registry_code
					? t.span(
							{
								class:
									"px-2 py-0.5 rounded-md bg-cyan-50 dark:bg-cyan-950 text-cyan-800 dark:text-cyan-300 border border-cyan-200 text-[11px] font-bold font-mono" +
									(annulled ? " line-through opacity-60" : ""),
							},
							doc.registry_code,
						)
					: t.span(),
				t.span(
					{
						class: doc.registered
							? "px-2 py-0.5 rounded-md bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-300 text-[11px] font-semibold"
							: "px-2 py-0.5 rounded-md bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 text-[11px] font-semibold",
					},
					doc.registered ? "Înregistrat" : "Generat",
				),
				// F6 (documentația scanată): badge-ul spune dintr-o privire CE e expus cetățenilor.
				doc.is_public
					? t.span(
							{
								class:
									"px-2 py-0.5 rounded-md bg-violet-50 text-violet-700 border border-violet-200 text-[11px] font-bold",
								title: "Vizibil și descărcabil în portalul public (/embed)",
							},
							"PUBLIC",
						)
					: t.span(),
				// Sare la parcela legată pe hartă (deep link cu zoom + evidențiere).
				doc.entity_id
					? t.a(
							{
								class: BTN_SM,
								href: `#/map?map=${encodeURIComponent(workspace.val.slug)}&feature=${doc.entity_id}`,
								title: "Vezi entitatea legată pe hartă",
							},
							icon("map-pin"),
						)
					: t.span(),
				t.a(
					{ class: BTN_SM, href: `/api/documents/${doc.id}/file`, title: "Descarcă" },
					icon("download-simple"),
				),
				// Rectificare: doar documentele generate din șablon, neanulate.
				doc.template_id && !annulled
					? t.button(
							{ class: BTN_SM, onclick: () => start_rectification(doc), title: "Rectifică" },
							icon("pencil-simple"),
						)
					: t.span(),
				// F6: expune/retrage în portalul public — doar documente pe hartă, ne-anulate.
				// Confirmare la EXPUNERE (documentele generate pot conține date personale — GDPR).
				doc.map_id && !annulled
					? t.button(
							{
								class: BTN_SM,
								title: doc.is_public
									? "Retrage documentul din portalul public"
									: "Expune documentul în portalul public (/embed) — verifică întâi că NU conține date personale",
								onclick: async () => {
									if (
										!doc.is_public &&
										!window.confirm(
											`Expui '${doc.name}' PUBLIC (oricine are linkul hărții îl poate descărca)?\n` +
												"Verifică întâi că nu conține date personale (nume, CNP, adrese de solicitanți).",
										)
									) {
										return
									}
									const r = await api_post(`/api/documents/${doc.id}/public`, {
										public: !doc.is_public,
									})
									if (r.ok) {
										show_toast(
											doc.is_public ? "Retras din portalul public." : "Expus în portalul public.",
											"ok",
										)
										await load_documents()
									} else {
										show_toast(`Eroare: ${r.err?.message ?? ""}`, "err")
									}
								},
							},
							icon(doc.is_public ? "eye-slash" : "globe"),
						)
					: t.span(),
				t.button({ class: BTN_SM, onclick: rename, title: "Redenumește" }, icon("cursor-text")),
				t.button(
					{ class: BTN_SM, onclick: toggle_history, title: "Istoric" },
					icon("clock-counter-clockwise"),
				),
				t.button({ class: BTN_SM, onclick: toggle_links, title: "Legături" }, icon("link")),
				// Documentele cu număr de registru se ANULEAZĂ (numărul rămâne); restul se șterg.
				doc.registry_code
					? annulled
						? t.span()
						: t.button(
								{ class: BTN_SM_DANGER, onclick: annul, title: "Anulează" },
								icon("prohibit"),
							)
					: t.button({ class: BTN_SM_DANGER, onclick: remove, title: "Șterge" }, icon("trash")),
			),
		),
		t.div(
			{ style: () => `display:${store.expanded_doc === doc.id ? "block" : "none"}` },
			() => (store.doc_history_visible === doc.id ? DocumentHistory() : t.span()),
			doc.registry_code === "" && !annulled ? RegisterInRegistry(doc) : t.span(),
			DocumentLinks(doc),
		),
	)
}

/** Istoricul versiunilor documentului expandat (citește store.doc_history). */
function DocumentHistory() {
	const type_label: Record<string, string> = {
		create: "creat",
		regenerate: "rectificat",
		update_meta: "redenumit / meta",
		annul: "anulat",
		update_config: "actualizat",
	}
	return t.div(
		{ class: "mt-3 border-t border-slate-100 dark:border-slate-800 pt-3" },
		t.h4(
			{
				class: "text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2",
			},
			"Istoric",
		),
		() =>
			store.doc_history_loading
				? t.p({ class: "text-xs text-slate-400 dark:text-slate-500" }, "Se încarcă…")
				: t.div(
						{ class: "flex flex-col gap-1" },
						...store.doc_history.map((v) =>
							t.div(
								{ class: "flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300" },
								t.span(
									{ class: "font-mono font-bold text-slate-400 dark:text-slate-500" },
									`v${v.version}`,
								),
								t.span(
									{
										class:
											CHIP_CLS +
											" bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300",
									},
									type_label[v.change_type] ?? v.change_type,
								),
								t.span({}, fmt_date(v.created_at)),
								v.message
									? t.span({ class: "text-slate-500 dark:text-slate-400 italic" }, `„${v.message}"`)
									: t.span(),
							),
						),
					),
	)
}

/** Înregistrare ulterioară: documentul (fără număr) primește numărul următor dintr-un registru. */
function RegisterInRegistry(doc: DocumentSummary) {
	const pick = { value: "" }
	async function submit() {
		if (!pick.value) {
			show_toast("Alege registrul.", "err")
			return
		}
		const r = await api_post<{ registry_code: string; warnings?: string[] }>(
			`/api/documents/${doc.id}/register`,
			{ map: workspace.val.slug, registry: pick.value },
		)
		if (r.ok) {
			// Numărul NU a ajuns în fișier (document încărcat, registru fără câmp de șablon…): serverul
			// o spune în `warnings` — se afișează ca atare, mai mult timp, în locul confirmării simple.
			const warnings = r.data.warnings ?? []
			if (warnings.length > 0) {
				show_toast(warnings.join(" "), "info", 12000)
			} else {
				show_toast(`Înregistrat cu numărul ${r.data.registry_code} — trecut și în document.`, "ok")
			}
			await load_documents()
			await load_registries()
		} else {
			show_toast(`Eroare: ${r.err?.message ?? ""}`, "err")
		}
	}
	return t.div(
		{ class: "mt-3 border-t border-slate-100 dark:border-slate-800 pt-3" },
		t.h4(
			{
				class: "text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2",
			},
			"Adaugă în registru",
		),
		t.div(
			{ class: "grid sm:grid-cols-3 gap-2 items-center" },
			t.div(
				{ class: "sm:col-span-2" },
				t.select(
					{
						class: SELECT_CLS,
						onchange: (e: Event) => {
							pick.value = (e.target as HTMLSelectElement).value
						},
					},
					() =>
						t.optgroup(
							{ label: `Registrele hărții ${workspace.val.name || "—"}` },
							t.option({ value: "" }, "— alege registrul —"),
							...store.registries.map((rg) =>
								t.option(
									{ value: rg.slug },
									`${rg.name || rg.slug} (ultimul: ${rg.last_code || "—"})`,
								),
							),
						),
				),
			),
			t.button({ class: BTN_SM, onclick: submit }, "Primește numărul următor"),
		),
		t.p(
			{ class: "text-[11px] text-slate-400 dark:text-slate-500 mt-1.5" },
			"Numărul e definitiv — la ștergerea documentului rămâne gol în registru (ca un număr anulat).",
		),
	)
}

/** Mesaj de gardă când nu e ales niciun workspace (documentele și registrele sunt per hartă). */
function WorkspaceGate() {
	return t.div(
		{
			class:
				"bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-xl p-6 text-center",
		},
		t.p(
			{ class: "text-sm font-semibold text-amber-800 dark:text-amber-300" },
			"Alege întâi o hartă de lucru.",
		),
		t.p(
			{ class: "text-xs text-amber-700 dark:text-amber-300 mt-1" },
			"Documentele și registrele aparțin unei hărți — ex. PUG Ștefan cel Mare.",
		),
		t.a(
			{
				href: "#/harti",
				class:
					"inline-block mt-4 px-4 py-2 rounded-lg bg-cyan-700 text-white text-sm font-medium hover:bg-cyan-800",
			},
			"Alege un spațiu de lucru",
		),
	)
}

/** Bară de căutare + filtre pentru lista de documente (debounce pe căutare). */
function DocumentFilters() {
	let debounce_timer = 0
	const search_input = t.input({
		type: "search",
		class: INPUT_CLS,
		placeholder: "Caută după nume sau număr de registru (ex. CU0003)…",
		value: () => store.filter_q,
		oninput: (e: Event) => {
			store.filter_q = (e.target as HTMLInputElement).value
			window.clearTimeout(debounce_timer)
			debounce_timer = window.setTimeout(load_documents, 300)
		},
	})
	return t.div(
		{ class: "grid sm:grid-cols-2 lg:grid-cols-4 gap-2" },
		search_input,
		t.select(
			{
				class: SELECT_CLS,
				onchange: (e: Event) => {
					store.filter_status = (e.target as HTMLSelectElement).value as "" | "emis" | "anulat"
					load_documents()
				},
			},
			t.option({ value: "" }, "Orice status"),
			t.option({ value: "emis" }, "Doar emise"),
			t.option({ value: "anulat" }, "Doar anulate"),
		),
		t.select(
			{
				class: SELECT_CLS,
				onchange: (e: Event) => {
					store.filter_template_id = (e.target as HTMLSelectElement).value
					load_documents()
				},
			},
			() =>
				t.optgroup(
					{ label: "Șablon" },
					t.option({ value: "" }, "Orice șablon"),
					...store.templates.map((x) => t.option({ value: x.id }, x.name || x.slug)),
				),
		),
		t.select(
			{
				class: SELECT_CLS,
				onchange: (e: Event) => {
					store.filter_registry_id = (e.target as HTMLSelectElement).value
					load_documents()
				},
			},
			() =>
				t.optgroup(
					{ label: "Registru" },
					t.option({ value: "" }, "Orice registru"),
					...store.registries.map((x) => t.option({ value: x.id }, x.name || x.slug)),
				),
		),
	)
}

function DocumentsTab() {
	return t.div(
		{
			class: "flex flex-col gap-4",
			style: () => `display:${store.active_tab === "documents" ? "flex" : "none"}`,
		},
		() => (workspace.val.slug ? t.span() : WorkspaceGate()),
		// Două coloane pe desktop: stânga = emiterea (generare + înregistrare),
		// dreapta = arhiva (căutare + listă cu scroll propriu). Pe mobil: stivă.
		// Ascuns fără workspace (rămâne doar garda de mai sus).
		t.div(
			{
				class: "grid lg:grid-cols-[minmax(24rem,1fr)_minmax(0,2fr)] gap-4 items-start",
				style: () => (workspace.val.slug ? "" : "display:none"),
			},
			t.div(
				{
					class:
						"flex flex-col gap-4 lg:sticky lg:top-14 lg:max-h-[calc(100vh-4.5rem)] lg:overflow-y-auto lg:pr-1",
				},
				GenerateForm(),
				RegisterForm(),
			),
			t.section(
				{ class: "flex flex-col gap-3 min-w-0" },
				t.div(
					{ class: "flex items-center justify-between gap-3 flex-wrap" },
					t.h2({ class: "text-sm font-bold text-slate-700 dark:text-slate-200" }, () =>
						workspace.val.slug ? `Documentele hărții ${workspace.val.name}` : "Documente",
					),
					t.p({ class: "text-xs text-slate-400 dark:text-slate-500" }, () =>
						store.documents.length > 0 ? `${store.documents.length} afișate` : "",
					),
				),
				DocumentFilters(),
				// Cipul filtrului pe entitate (deep link din hartă): spune ce vezi și cum lărgești.
				() =>
					store.filter_entity_id
						? t.div(
								{ class: "flex items-center gap-2 flex-wrap text-xs" },
								t.span(
									{
										class:
											"inline-flex items-center gap-2 rounded-full bg-cyan-50 dark:bg-cyan-950 border border-cyan-200 dark:border-cyan-800 text-cyan-800 dark:text-cyan-200 px-3 py-1 font-medium",
									},
									`Doar documentele entității ${store.filter_entity_label}`,
									t.button(
										{
											class: "font-bold hover:text-red-600",
											title: "Arată toate documentele hărții",
											onclick: () => {
												store.filter_entity_id = ""
												store.filter_entity_label = ""
												store.highlight_doc_id = ""
												load_documents()
											},
										},
										"✕",
									),
								),
							)
						: t.span(),
				// Lista derulează independent — pagina nu crește la sute de documente.
				() =>
					t.div(
						{
							class:
								"flex flex-col gap-2.5 lg:max-h-[calc(100vh-13rem)] lg:overflow-y-auto lg:pr-1",
						},
						store.documents_loading
							? t.p({ class: "text-sm text-slate-500 dark:text-slate-400" }, "Se încarcă…")
							: t.span(),
						...store.documents.map((d) => DocumentRow(d)),
						!store.documents_loading && store.documents.length === 0
							? t.p(
									{ class: "text-sm text-slate-500 dark:text-slate-400" },
									store.filter_q ||
										store.filter_status ||
										store.filter_template_id ||
										store.filter_registry_id
										? "Niciun document nu se potrivește filtrelor."
										: "Niciun document încă.",
								)
							: t.span(),
					),
			),
		),
	)
}

// ─────────────────────────────────────────────
// Tab Registre
// ─────────────────────────────────────────────

/** Ce înseamnă un registru fără câmp de șablon (M2-O1) — același text în formular, pe card și la generare. */
const NO_NUMBER_FIELD_NOTE = "numărul apare în registru și în numele actului, dar nu pe hârtie"

function RegistryCreateForm() {
	// Previzualizare live a codului (reactivă pe prefix/sufix/padding).
	const prefix_state = van.state("")
	const suffix_state = van.state("")
	const padding_state = van.state(4)
	let slug_touched = false // utilizatorul a editat identificatorul manual

	const name_input = t.input({
		type: "text",
		class: INPUT_CLS,
		placeholder: "ex: Registru certificate de urbanism 2026",
		oninput: (e: Event) => {
			if (!slug_touched) {
				slug_input.value = slugify((e.target as HTMLInputElement).value)
			}
		},
	})
	const slug_input = t.input({
		type: "text",
		class: INPUT_CLS,
		placeholder: "se generează din nume",
		oninput: () => {
			slug_touched = true
		},
	})
	const prefix_input = t.input({
		type: "text",
		class: INPUT_CLS,
		placeholder: "ex: CU",
		oninput: (e: Event) => {
			prefix_state.val = (e.target as HTMLInputElement).value.trim()
		},
	})
	const suffix_input = t.input({
		type: "text",
		class: INPUT_CLS,
		placeholder: "ex: /2026 (opțional)",
		oninput: (e: Event) => {
			suffix_state.val = (e.target as HTMLInputElement).value.trim()
		},
	})
	const padding_input = t.input({
		type: "number",
		class: INPUT_CLS,
		value: "4",
		min: "1",
		max: "8",
		oninput: (e: Event) => {
			padding_state.val = Number((e.target as HTMLInputElement).value) || 4
		},
	})
	// Câmpul de șablon care primește numărul (M2-O1): fără el, codul apare în registru și în numele
	// actului, dar rubrica „Nr. ___" de pe hârtie rămâne goală — formularul o spune cât e gol.
	const number_field_state = van.state("")
	const number_field_input = t.input({
		type: "text",
		class: INPUT_CLS,
		placeholder: "ex: nr_certificat",
		oninput: (e: Event) => {
			number_field_state.val = (e.target as HTMLInputElement).value.trim()
		},
	})

	async function submit() {
		const slug = slug_input.value.trim() || slugify(name_input.value)
		if (!name_input.value.trim() || !slug || !prefix_input.value.trim()) {
			show_toast("Completează numele și prefixul codului.", "err")
			return
		}
		const r = await api_post<RegistrySummary>(
			`/api/maps/${encodeURIComponent(workspace.val.slug)}/registries`,
			{
				slug,
				name: name_input.value.trim(),
				prefix: prefix_input.value.trim(),
				suffix: suffix_input.value.trim() || undefined,
				padding: Number(padding_input.value) || 4,
				number_field: number_field_input.value.trim() || undefined,
			},
		)
		if (r.ok) {
			show_toast(`Registru '${r.data.name}' creat.`, "ok")
			name_input.value = ""
			slug_input.value = ""
			prefix_input.value = ""
			suffix_input.value = ""
			number_field_input.value = ""
			number_field_state.val = ""
			prefix_state.val = ""
			suffix_state.val = ""
			slug_touched = false
			await load_registries()
		} else {
			show_toast(`Eroare: ${r.err?.message ?? ""}`, "err")
		}
	}

	return t.section(
		{
			class:
				"bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-5",
		},
		t.h2({ class: "text-sm font-bold text-slate-700 dark:text-slate-200 mb-1" }, "Registru nou"),
		t.p(
			{ class: "text-xs text-slate-500 dark:text-slate-400 mb-4" },
			"Un registru = un contor continuu (numerele nu se refolosesc niciodată). Pentru un an nou, creează pur și simplu alt registru — folosește sufixul ca să distingi codurile (ex. CU0001/2026).",
		),
		t.div(
			{ class: "grid sm:grid-cols-2 lg:grid-cols-3 gap-3" },
			Field("Numele registrului", name_input),
			Field("Prefixul codului", prefix_input),
			Field("Sufixul codului (opțional)", suffix_input),
		),
		// Previzualizare live: utilizatorul vede exact cum vor arăta codurile.
		t.p(
			{ class: "text-xs mt-2" },
			t.span({ class: "text-slate-500 dark:text-slate-400" }, "Primul document va primi numărul: "),
			t.span({ class: "font-mono font-bold text-cyan-800 dark:text-cyan-300" }, () =>
				prefix_state.val
					? `${prefix_state.val}${"1".padStart(padding_state.val, "0")}${suffix_state.val}`
					: "— completează prefixul —",
			),
		),
		t.details(
			{ class: "mt-3" },
			t.summary(
				{
					class:
						"text-xs font-semibold text-slate-500 dark:text-slate-400 cursor-pointer hover:text-slate-700",
				},
				"Setări avansate",
			),
			t.div(
				{ class: "grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3" },
				Field("Identificator (se generează din nume)", slug_input),
				Field("Lățimea numărului (cifre)", padding_input),
				Field("Câmp de șablon pt. număr", number_field_input),
			),
			t.p(
				{ class: "text-[11px] text-slate-400 dark:text-slate-500 mt-2" },
				"Câmpul de șablon (ex. nr_certificat) primește automat codul la generare.",
			),
		),
		// ÎN AFARA „Setărilor avansate" (care stau închise): avertismentul trebuie văzut fără a le deschide.
		t.p(
			{
				id: "registry-no-field-warning",
				class: () =>
					"mt-3 text-xs font-medium text-amber-700 dark:text-amber-300 " +
					(number_field_state.val ? "hidden" : ""),
			},
			`Fără câmp de șablon pentru număr: ${NO_NUMBER_FIELD_NOTE}. ` +
				"Completează-l la „Setări avansate” (ex. nr_certificat) sau, mai târziu, pe cardul registrului.",
		),
		t.div(
			{ class: "mt-4" },
			t.button({ class: BTN_PRIMARY, onclick: submit }, icon("plus-circle"), "Creează registrul"),
		),
	)
}

function RegistryCard(rg: RegistrySummary) {
	async function remove() {
		if (
			!window.confirm(
				`Ștergi registrul '${rg.name || rg.slug}'? Numerele alocate rămân pe documente.`,
			)
		) {
			return
		}
		const r = await api_delete(
			`/api/maps/${encodeURIComponent(workspace.val.slug)}/registries/${encodeURIComponent(rg.slug)}`,
		)
		if (r.ok) {
			show_toast("Registru șters.", "ok")
			await load_registries()
		} else {
			show_toast(`Eroare: ${r.err?.message ?? ""}`, "err")
		}
	}

	function toggle_entries() {
		if (store.expanded_registry === rg.slug) {
			store.expanded_registry = ""
		} else {
			store.expanded_registry = rg.slug
			load_registry_entries(rg.slug)
		}
	}

	const sample = `${rg.prefix}${"1".padStart(rg.padding, "0")}${rg.suffix}`

	// Câmpul de șablon se poate schimba și DUPĂ creare (M2-O1; ruta PATCH exista, interfața nu):
	// un registru creat fără el nu mai trebuie șters și refăcut ca numărul să ajungă pe hârtie.
	const field_edit = t.input({
		type: "text",
		// clasă proprie (nu INPUT_CLS + suprascrieri): două utilitare de aceeași familie pe același
		// element se decid după ordinea din CSS, nu după cea din atribut
		class:
			"w-56 border border-slate-300 dark:border-slate-600 rounded-lg px-2.5 py-1.5 text-xs " +
			"text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900 placeholder:text-slate-400 " +
			"focus:outline-none focus:border-cyan-600",
		placeholder: "ex: nr_certificat",
		value: rg.number_field,
		"aria-label": "Câmp de șablon pentru număr",
	}) as HTMLInputElement
	async function save_number_field() {
		const number_field = field_edit.value.trim()
		const r = await api_patch(
			`/api/maps/${encodeURIComponent(workspace.val.slug)}/registries/${encodeURIComponent(rg.slug)}`,
			{ number_field },
		)
		if (r.ok) {
			show_toast(
				number_field
					? `Registrul '${rg.name || rg.slug}' scrie de acum numărul în câmpul {${number_field}}.`
					: `Registrul '${rg.name || rg.slug}' nu mai are câmp de șablon: ${NO_NUMBER_FIELD_NOTE}.`,
				number_field ? "ok" : "info",
			)
			await load_registries()
		} else {
			show_toast(`Eroare: ${r.err?.message ?? ""}`, "err")
		}
	}

	return t.div(
		{
			class:
				"bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-5",
		},
		t.div(
			{ class: "flex items-start justify-between gap-3 flex-wrap" },
			t.div(
				t.h3({ class: "text-sm font-bold text-slate-800 dark:text-slate-200" }, rg.name || rg.slug),
				t.p(
					{ class: "text-xs text-slate-500 dark:text-slate-400 mt-0.5" },
					`${rg.slug} · format ${sample} · ${rg.document_count} documente` +
						(rg.last_code ? ` · ultimul: ${rg.last_code}` : "") +
						(rg.number_field ? ` · injectează {${rg.number_field}}` : ""),
				),
				// Marcajul registrelor fără câmp de șablon — vizibil în listă, nu doar la generare.
				rg.number_field
					? t.span()
					: t.p(
							{
								class:
									"registry-no-field inline-block mt-1.5 px-2 py-0.5 rounded-md bg-amber-50 dark:bg-amber-950 " +
									"border border-amber-200 dark:border-amber-800 text-[11px] font-semibold text-amber-800 dark:text-amber-300",
							},
							`fără câmp de șablon — ${NO_NUMBER_FIELD_NOTE}`,
						),
				rg.description
					? t.p({ class: "text-xs text-slate-600 dark:text-slate-300 mt-1" }, rg.description)
					: t.span(),
				t.div(
					{ class: "flex items-center gap-2 flex-wrap mt-2" },
					t.span(
						{ class: "text-[11px] text-slate-500 dark:text-slate-400" },
						"Câmp de șablon pt. număr:",
					),
					field_edit,
					t.button({ class: BTN_SM, onclick: save_number_field }, "Salvează câmpul"),
				),
			),
			t.div(
				{ class: "flex items-center gap-2 flex-wrap" },
				t.button(
					{
						class: BTN_SM,
						onclick: () => {
							store.gen_registry = rg.slug
							navigate("/documents")
						},
					},
					icon("file-plus"),
					"Generează în registru",
				),
				t.button({ class: BTN_SM, onclick: toggle_entries }, () =>
					store.expanded_registry === rg.slug ? "Ascunde registrul" : "Vezi registrul",
				),
				t.button({ class: BTN_SM_DANGER, onclick: remove }, icon("trash"), "Șterge"),
			),
		),
		// Tabelul legal: numerele în ordine, cu golurile vizibile.
		t.div(
			{ style: () => `display:${store.expanded_registry === rg.slug ? "block" : "none"}` },
			() => {
				if (store.expanded_registry !== rg.slug) {
					return t.span()
				}
				if (store.registry_entries_loading) {
					return t.p({ class: "text-xs text-slate-400 dark:text-slate-500 mt-3" }, "Se încarcă…")
				}
				if (store.registry_entries.length === 0) {
					return t.p(
						{ class: "text-xs text-slate-400 dark:text-slate-500 mt-3" },
						"Registru gol — niciun număr alocat.",
					)
				}
				return t.div(
					{ class: "mt-3 border-t border-slate-100 dark:border-slate-800 pt-3 overflow-x-auto" },
					t.table(
						{ class: "w-full text-xs" },
						t.thead(
							t.tr(
								{ class: "text-left text-slate-400 dark:text-slate-500 uppercase tracking-wide" },
								t.th({ class: "py-1.5 pr-3 font-bold" }, "Nr."),
								t.th({ class: "py-1.5 pr-3 font-bold" }, "Document"),
								t.th({ class: "py-1.5 pr-3 font-bold" }, "Data"),
								t.th({ class: "py-1.5 font-bold" }, ""),
							),
						),
						t.tbody(
							...store.registry_entries.map((e) =>
								t.tr(
									{
										class:
											"border-t border-slate-100 dark:border-slate-800" +
											(e.status === "anulat" ? " opacity-60" : ""),
									},
									t.td(
										{
											class:
												"py-1.5 pr-3 font-mono font-bold " +
												(e.status === "anulat"
													? "text-red-700 dark:text-red-300 line-through"
													: "text-cyan-800 dark:text-cyan-300"),
										},
										e.registry_code,
									),
									t.td(
										{ class: "py-1.5 pr-3 text-slate-700 dark:text-slate-200" },
										e.name || e.filename,
										e.status === "anulat"
											? t.span(
													{
														class:
															"ml-2 px-1.5 py-0.5 rounded bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 text-[10px] font-bold",
													},
													"ANULAT",
												)
											: t.span(),
									),
									t.td(
										{ class: "py-1.5 pr-3 text-slate-500 dark:text-slate-400" },
										fmt_date(e.created_at),
									),
									t.td(
										{ class: "py-1.5 text-right" },
										t.a(
											{
												class: "text-cyan-700 dark:text-cyan-300 hover:text-cyan-900 font-semibold",
												href: `/api/documents/${e.id}/file`,
											},
											"Descarcă",
										),
									),
								),
							),
						),
					),
				)
			},
		),
	)
}

function RegistriesTab() {
	return t.div(
		{
			class: "flex flex-col gap-4",
			style: () => `display:${store.active_tab === "registries" ? "flex" : "none"}`,
		},
		() => (workspace.val.slug ? t.span() : WorkspaceGate()),
		() => (workspace.val.slug ? RegistryCreateForm() : t.span()),
		() =>
			t.div(
				{ class: "flex flex-col gap-3" },
				store.registries_loading
					? t.p({ class: "text-sm text-slate-500 dark:text-slate-400" }, "Se încarcă…")
					: t.span(),
				...store.registries.map((rg) => RegistryCard(rg)),
				!store.registries_loading && workspace.val.slug && store.registries.length === 0
					? t.p(
							{ class: "text-sm text-slate-500 dark:text-slate-400" },
							"Niciun registru pe această hartă — creează primul mai sus.",
						)
					: t.span(),
			),
	)
}

// ─────────────────────────────────────────────
// Rapoarte (F5-T9): agregate per hartă + export CSV per tabel
// ─────────────────────────────────────────────

async function load_rapoarte() {
	if (!workspace.val.slug) {
		store.rapoarte = null
		return
	}
	store.rapoarte_loading = true
	const r = await api_get<Rapoarte>(`/api/maps/${encodeURIComponent(workspace.val.slug)}/rapoarte`)
	store.rapoarte = r.ok ? r.data : null
	store.rapoarte_error = r.ok ? "" : (r.err?.message ?? "Rapoartele nu au putut fi încărcate.")
	store.rapoarte_loading = false
}

/** Descarcă un CSV (BOM + separator „;" — deschide corect în Excel cu setări RO). */
function csv_download(filename: string, headers: string[], rows: Array<Array<string | number>>) {
	const esc = (v: string | number) => {
		const s = String(v)
		return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
	}
	const csv = "\uFEFF" + [headers, ...rows].map((r) => r.map(esc).join(";")).join("\n")
	const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }))
	const a = document.createElement("a")
	a.href = url
	a.download = filename
	a.click()
	URL.revokeObjectURL(url)
}

/** Card statistică (numărul mare + eticheta). */
function stat_card(label: string, value: () => string | number) {
	return t.div(
		{
			class:
				"rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 flex-1 min-w-36",
		},
		t.div({ class: "text-2xl font-extrabold text-slate-900 dark:text-slate-100" }, () =>
			String(value()),
		),
		t.div({ class: "text-xs text-slate-500 dark:text-slate-400 mt-0.5" }, label),
	)
}

/** Tabel de raport cu buton de export CSV. `rows` vine ca funcție (reactiv la reload).
 *  `row_href` (opțional): deep-link per rând — prima celulă devine link (CSV-ul rămâne neschimbat). */
function raport_table(
	title: string,
	headers: string[],
	get_rows: () => Array<Array<string | number>>,
	csv_name: string,
	row_href?: (row_index: number) => string,
) {
	return t.div(
		{
			class:
				"rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden",
		},
		t.div(
			{
				class:
					"flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800",
			},
			t.div({ class: "text-sm font-bold text-slate-800 dark:text-slate-200" }, title),
			t.button(
				{
					class:
						"text-xs font-semibold text-cyan-700 dark:text-cyan-300 hover:underline cursor-pointer",
					onclick: () => csv_download(csv_name, headers, get_rows()),
				},
				"⬇ CSV",
			),
		),
		t.div({ class: "overflow-x-auto" }, () => {
			const rows = get_rows()
			if (rows.length === 0) {
				return t.div(
					{ class: "px-4 py-6 text-sm text-slate-400 dark:text-slate-500" },
					"Nimic de raportat încă.",
				)
			}
			return t.table(
				{ class: "w-full text-sm" },
				t.thead(
					t.tr(
						{
							class: "text-left text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400",
						},
						...headers.map((h) => t.th({ class: "px-4 py-2 font-semibold" }, h)),
					),
				),
				t.tbody(
					...rows.map((r, i) => {
						const href = row_href ? row_href(i) : ""
						return t.tr(
							{ class: "border-t border-slate-100 dark:border-slate-800" },
							...r.map((c, ci) =>
								t.td(
									{ class: "px-4 py-2 text-slate-700 dark:text-slate-200" },
									ci === 0 && href
										? t.a(
												{
													href,
													class: "text-cyan-700 dark:text-cyan-300 hover:underline font-medium",
												},
												String(c),
											)
										: String(c),
								),
							),
						)
					}),
				),
			)
		}),
	)
}

const STATUS_RO: Record<string, string> = {
	new: "Nouă",
	assigned: "Preluată",
	in_progress: "În lucru",
	answered: "Răspuns",
	issued: "Emisă",
	closed: "Închisă",
	rejected: "Respinsă",
}
const TIP_ACT_RO: Record<string, string> = {
	sesizare: "Sesizare",
	cu_informare: "CU de informare",
	aviz: "Aviz",
	observatie_consultare: "Observație consultare",
}

function RapoarteTab() {
	const d = () => store.rapoarte
	const suma = (xs: Array<{ total: number }>) => xs.reduce((s, x) => s + x.total, 0)
	return t.div(
		{
			class: "flex flex-col gap-4",
			style: () => `display:${store.active_tab === "rapoarte" ? "flex" : "none"}`,
		},
		() => (workspace.val.slug ? t.span() : WorkspaceGate()),
		() =>
			store.rapoarte_loading
				? t.p({ class: "text-sm text-slate-500 dark:text-slate-400" }, "Se încarcă rapoartele…")
				: t.span(),
		() =>
			!store.rapoarte_loading && store.rapoarte_error
				? t.p(
						{
							class:
								"text-sm text-amber-700 dark:text-amber-400 border border-amber-300 dark:border-amber-700 rounded-md px-3 py-2",
						},
						store.rapoarte_error,
					)
				: t.span(),
		// Carduri sumare
		t.div({ class: "flex gap-3 flex-wrap" }, () => {
			const r = d()
			if (!r) {
				return t.span()
			}
			const deschise = r.cereri_pe_stare
				.filter((x) => ["new", "assigned", "in_progress", "answered"].includes(x.status))
				.reduce((s, x) => s + x.total, 0)
			return t.div(
				{ class: "flex gap-3 flex-wrap flex-1" },
				stat_card("acte generate", () => suma(d()?.cu_pe_luna ?? [])),
				stat_card("cereri în lucru", () => deschise),
				stat_card(
					"acte emise (cereri)",
					() => d()?.cereri_pe_stare.find((x) => x.status === "issued")?.total ?? 0,
				),
				stat_card("UTR-uri în bilanț", () => d()?.bilant_utr.length ?? 0),
			)
		}),
		raport_table(
			"Acte generate pe lună",
			["Luna", "Total"],
			() => (d()?.cu_pe_luna ?? []).map((x) => [x.luna, x.total]),
			"acte_pe_luna.csv",
		),
		t.div(
			{ class: "grid md:grid-cols-2 gap-4" },
			raport_table(
				"Cereri pe stare",
				["Stare", "Total"],
				() => (d()?.cereri_pe_stare ?? []).map((x) => [STATUS_RO[x.status] ?? x.status, x.total]),
				"cereri_pe_stare.csv",
			),
			raport_table(
				"Cereri pe tip de act",
				["Tip act", "Total"],
				() => (d()?.cereri_pe_tip ?? []).map((x) => [TIP_ACT_RO[x.tip_act] ?? x.tip_act, x.total]),
				"cereri_pe_tip.csv",
			),
		),
		raport_table(
			"Bilanț UTR (arie geodezică + indicatori RLU)",
			["UTR", "Denumire", "Poligoane", "Arie", "POT max", "CUT max", "Rh max"],
			() =>
				(d()?.bilant_utr ?? []).map((x) => [
					x.cod || "(fără cod)",
					x.denumire,
					x.poligoane,
					format_area(x.arie_m2),
					String(x.pot_max ?? ""),
					String(x.cut_max ?? ""),
					String(x.rh_max ?? ""),
				]),
			"bilant_utr.csv",
		),
		raport_table(
			"Documente pe registru",
			["Registru", "Documente", "Ultimul număr"],
			() =>
				(d()?.documente_pe_registru ?? []).map((x) => [
					x.nume || x.slug,
					x.documente,
					x.ultimul_numar,
				]),
			"documente_pe_registru.csv",
		),
		// Observațiile din consultarea publică PUG (F6-T6) — listă nominală cu deep-link pe cerere,
		// pe lângă agregatul din „Cereri pe tip de act".
		raport_table(
			"Observații din consultarea publică",
			["Subiect", "Consultarea", "Stare", "Depusă la"],
			() =>
				(d()?.observatii_consultare ?? []).map((x) => [
					x.subject,
					// Bannerul scrie `consultare:<slug>` — afișăm doar slug-ul; text liber trece ca atare.
					x.category.replace(/^consultare:/, ""),
					STATUS_RO[x.status] ?? x.status,
					x.created_at,
				]),
			"observatii_consultare.csv",
			(i) => {
				const o = (d()?.observatii_consultare ?? [])[i]
				return o ? `#/solicitari?id=${encodeURIComponent(o.id)}` : ""
			},
		),
	)
}

// ─────────────────────────────────────────────
// Shell pagină — secțiunea vine din rută (#/documents, #/registre, #/sabloane);
// header-ul global e în shell-ul aplicației (app.client.ts).
// ─────────────────────────────────────────────

const PAGE_META = {
	documents: {
		title: "Documente",
		sub: "Generare din șabloane, înregistrare manuală, numere de registru și legături.",
	},
	registries: {
		title: "Registre",
		sub: "Registre de documente per hartă, cu numerotare incrementală (numerele nu se refolosesc).",
	},
	templates: {
		title: "Șabloane",
		sub: "Șabloane docx ale primăriei și globale: upload, versiuni, câmpuri și autofill.",
	},
	rapoarte: {
		title: "Rapoarte",
		sub: "Acte pe lună, cereri pe stare/tip, bilanț UTR și registre — cu export CSV.",
	},
} as const

function tab_from_path(path: string): "templates" | "documents" | "registries" | "rapoarte" {
	if (path === "/sabloane") {
		return "templates"
	}
	if (path === "/registre") {
		return "registries"
	}
	if (path === "/rapoarte") {
		return "rapoarte"
	}
	return "documents"
}

export function DocumentsPage() {
	store.active_tab = tab_from_path(current_path.val)
	org_names_state = "idle" // numele primăriilor se aduc o dată per montare (vezi load_org_names)
	// Deep link din panoul hărții: #/documents?entity=<id>&template=<slug> —
	// entitatea s-a ales PE HARTĂ; aici doar se afișează + se preselectează șablonul.
	const qp = get_hash_query_params()
	const entity_param = qp.get("entity") ?? ""
	// Lista se restrânge la documentele entității (cipul de sus o lărgește la loc); fără parametru
	// (navigare din meniu) filtrul se șterge — pagina nu rămâne blocată pe parcela de data trecută.
	store.filter_entity_id = entity_param
	store.filter_entity_label = entity_param ? entity_param.slice(0, 8) : ""
	store.highlight_doc_id = qp.get("doc") ?? ""
	store.highlight_scroll = store.highlight_doc_id !== ""
	if (entity_param !== store.gen_entity.id) {
		load_gen_entity(entity_param)
	} else if (entity_param) {
		// entitatea e deja încărcată (a doua venire din același popup) — eticheta cipului din ce avem
		const e = store.gen_entity
		store.filter_entity_label = entity_chip_label(e.id, e.name, e.layer_name)
	}
	const template_param = qp.get("template") ?? ""
	if (template_param) {
		gen_inputs = {}
		store.gen_template = template_param
	}
	const el = t.div(
		{
			class:
				"min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-800 dark:text-slate-200 antialiased",
		},
		t.div({
			id: "doc-toast-area",
			class: "fixed top-14 right-6 z-50 flex flex-col gap-2 pointer-events-none",
		}),
		t.main(
			{ class: "w-full px-3 sm:px-6 py-4 sm:py-6 flex flex-col gap-4 sm:gap-6" },
			t.div(
				{ class: "flex items-center justify-between gap-3 flex-wrap" },
				t.div(
					t.h1({ class: "text-xl font-extrabold text-slate-900 dark:text-slate-100" }, () => {
						const meta = PAGE_META[store.active_tab]
						const ws = workspace.val.name
						return ws && store.active_tab !== "templates" ? `${meta.title} — ${ws}` : meta.title
					}),
					t.p(
						{ class: "text-sm text-slate-500 dark:text-slate-400 mt-0.5" },
						() => PAGE_META[store.active_tab].sub,
					),
				),
			),
			TemplatesTab(),
			DocumentsTab(),
			RegistriesTab(),
			RapoarteTab(),
		),
	)
	// Încarcă datele după montarea shell-ului.
	queueMicrotask(() => {
		load_templates()
		load_documents()
		load_registries()
		void load_rapoarte()
		load_maps()
	})
	return el
}
